/**
 * Fills the cart toward the minimum-order or free-shipping threshold
 * with in-stock singles. Thresholds come from window.kl24ShippingRules.
 */
(() => {
  const root = document.querySelector('[data-karten-auffueller]');
  if (!root) return;

  const CACHE_KEY = 'kl24-auffueller-catalog-v3';
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const TOLERANCE_CENTS = 50;
  /** Max overshoot when topping up free/custom so sum can cross the target. */
  const OVERSHOOT_CENTS = 400;
  const LARGE_GAP_CENTS = 800;
  const MAX_DRAW = 40;
  const MAX_TOTAL = 250;
  /** Hard cap for Kl24InventoryReservation.filterAvailableCards scrub. */
  const AVAIL_TIMEOUT_MS = 10_000;
  /** One Shopify add covers a full Auffüller fill (MAX_TOTAL); avoids sequential section HTML. */
  const ADD_BATCH = 250;
  const CART_SECTIONS = ['cart-icon-bubble', 'cart-drawer'];

  const PM_SKU_FULL = /^PM-([A-Z0-9]+)-(\d+)-(\d+)-([A-Z]{2,4})(?:-(?:RH|H|STD))?(?:-EN)?$/i;
  const PM_SKU_SHORT = /^PM-([A-Z0-9]+)-(\d+)-([A-Z]{2,4})(?:-(?:RH|H|STD))?(?:-EN)?$/i;
  const POK_SKU = /^POK-([A-Z0-9]+)-(\d+)-([A-Z]{2,4})$/i;
  const CARD_NUM_RE = /(\d+)\s*\/\s*(\d+)/;

  const ENERGY_TYPES = [
    'feuer',
    'wasser',
    'pflanze',
    'elektro',
    'psycho',
    'kampf',
    'finsternis',
    'metall',
    'fee',
    'drache',
    'farblos',
  ];
  const ENERGY_LABELS = {
    feuer: 'Feuer',
    wasser: 'Wasser',
    pflanze: 'Pflanze',
    elektro: 'Elektro',
    psycho: 'Psycho',
    kampf: 'Kampf',
    finsternis: 'Finsternis',
    metall: 'Metall',
    fee: 'Fee',
    drache: 'Drache',
    farblos: 'Farblos',
  };

  const energyPaint = (label) => {
    const key = String(label || '').trim().toLowerCase();
    const slug = ENERGY_LABELS[key]
      ? key
      : Object.entries(ENERGY_LABELS).find(([, name]) => name.toLowerCase() === key)?.[0] || '';
    if (!slug) return { slug: '', color: 'rgb(var(--kl24-accent))', ink: 'rgb(var(--kl24-accent-ink))' };
    return {
      slug,
      color: `rgb(var(--kl24-energy-${slug}))`,
      ink: `rgb(var(--kl24-energy-${slug}-ink))`,
    };
  };

  const syncLotEnergy = (label) => {
    root.style.setProperty('--lot-energy', label ? energyPaint(label).color : 'rgb(var(--kl24-accent))');
  };
  const STATIC_SKU_ALIASES = {
    RSV: 'WHT',
    RSV10: 'WHT',
    RSV10PT5: 'WHT',
    RSV10PTS: 'WHT',
    'WHITE FLARE': 'WHT',
    WHITEFLARE: 'WHT',
    'WHITE-FLARE': 'WHT',
    'SV10.5W': 'WHT',
    SV105W: 'WHT',
    ZSV: 'BLK',
    ZSV10: 'BLK',
    ZSV10PT5: 'BLK',
    ZSV10PTS: 'BLK',
    'BLACK BOLT': 'BLK',
    BLACKBOLT: 'BLK',
    'BLACK-BOLT': 'BLK',
    'SV10.5B': 'BLK',
    SV105B: 'BLK',
    SWSH12PT5: 'CRZ',
    'SWSH12.5': 'CRZ',
    SV4PT5: 'PAF',
    SV04PT5: 'PAF',
    'SV04.5': 'PAF',
    SM115: 'SM115',
    HIF: 'SM115',
  };

  const DISPLAY_NAME_FALLBACKS = {
    SM115: 'Verborgenes Schicksal',
    HIF: 'Verborgenes Schicksal',
  };

  /** @type {Map<string, string>} */
  let skuToCanonical = new Map();

  const rememberAlias = (map, from, to) => {
    const key = String(from || '').trim().toUpperCase();
    const canonical = String(to || '').trim().toUpperCase();
    if (key && canonical) map.set(key, canonical);
  };

  const skuCodesForTcgdex = (tcgdexId) => {
    const tcg = String(tcgdexId || '').trim().toLowerCase();
    if (!tcg) return [];
    const codes = [tcg.toUpperCase()];
    const addSeries = (prefix, rest) => {
      codes.push(`${prefix}${rest.toUpperCase()}`);
      if (/^\d+$/.test(rest)) codes.push(`${prefix}${Number.parseInt(rest, 10)}`);
      if (!rest.includes('.')) return;
      const [major, minor] = rest.split('.');
      if (!/^\d+$/.test(major) || !minor) return;
      const majorInt = Number.parseInt(major, 10);
      codes.push(`${prefix}${majorInt}PT${minor.toUpperCase()}`);
      codes.push(`${prefix}${major.toUpperCase()}PT${minor.toUpperCase()}`);
      codes.push(`${prefix}${rest.replace('.', '').toUpperCase()}`);
    };
    if (tcg.startsWith('sv')) addSeries('SV', tcg.slice(2));
    else if (tcg.startsWith('swsh')) addSeries('SWSH', tcg.slice(4));
    else if (tcg.startsWith('sm') && tcg !== 'smp') addSeries('SM', tcg.slice(2));
    else if (tcg.startsWith('me') && tcg !== 'mep' && tcg !== 'mee') addSeries('ME', tcg.slice(2));
    return codes;
  };

  const rebuildSkuAliases = (index) => {
    const map = new Map();
    (index || []).forEach((entry) => {
      const setCode = String(entry.set_code || '').trim().toUpperCase();
      if (!setCode) return;
      rememberAlias(map, setCode, setCode);
      rememberAlias(map, entry.handle, setCode);
      skuCodesForTcgdex(entry.tcgdex_id).forEach((code) => rememberAlias(map, code, setCode));
    });
    Object.entries(STATIC_SKU_ALIASES).forEach(([from, to]) => rememberAlias(map, from, to));
    skuToCanonical = map;
  };

  const normalizeSetCode = (value) => {
    const raw = String(value || '').trim().toUpperCase();
    if (!raw) return raw;
    return skuToCanonical.get(raw) || raw;
  };

  const ENERGY_EN_TO_DE = {
    fire: 'Feuer',
    water: 'Wasser',
    grass: 'Pflanze',
    lightning: 'Elektro',
    psychic: 'Psycho',
    fighting: 'Kampf',
    darkness: 'Finsternis',
    metal: 'Metall',
    fairy: 'Fee',
    dragon: 'Drache',
    colorless: 'Farblos',
  };

  const collectionHandle = root.dataset.collectionHandle || 'pokemon-singles';
  const shippingCountry = String(window.kl24ShippingRules?.countryIso || 'DE').toUpperCase();
  const rules = {
    minOrderCents: Number(window.kl24ShippingRules?.minOrderCents) || 500,
    freeShippingCents:
      shippingCountry === 'AT'
        ? Number(window.kl24ShippingRules?.freeShippingFromCentsAt) || 5900
        : Number(window.kl24ShippingRules?.freeShippingFromCentsDe) ||
          Number(window.kl24ShippingRules?.freeShippingFromCents) ||
          3900,
  };

  const statusEl = root.querySelector('[data-fill-status]');
  const startEl = root.querySelector('[data-fill-start]');
  const summaryEl = root.querySelector('[data-fill-summary]');
  const poolEl = root.querySelector('[data-fill-pool]');
  const listEl = root.querySelector('[data-fill-list]');
  const feedbackEl = root.querySelector('[data-fill-feedback]');
  const addBtn = root.querySelector('[data-fill-add]');
  const rollBtn = root.querySelector('[data-fill-roll]');
  const wishSearch = root.querySelector('[data-fill-wish-search]');
  const wishResults = root.querySelector('[data-fill-wish-results]');
  const wishPicked = root.querySelector('[data-fill-wish-picked]');
  const setsEl = root.querySelector('[data-fill-sets]');
  const energyWrap = root.querySelector('[data-fill-energy-wrap]');
  const energySelect = root.querySelector('[data-fill-energy]');
  const typeWrap = root.querySelector('[data-fill-type-wrap]');
  const typeSelect = root.querySelector('[data-fill-type]');
  const holoInput = root.querySelector('[data-fill-holo]');
  const commonsInput = root.querySelector('[data-fill-commons]');
  const cartSetInput = root.querySelector('[data-fill-cart-set]');
  const excludeCartInput = root.querySelector('[data-fill-exclude-cart]');
  const amountInput = root.querySelector('[data-fill-amount]');
  const minLabel = root.querySelector('[data-fill-min-label]');
  const freeLabel = root.querySelector('[data-fill-free-label]');

  /** @type {Array<object>} */
  let catalog = [];
  /** @type {Array<object>} */
  let setIndex = [];
  /** @type {Array<object>} */
  let wishCards = [];
  /** @type {Array<object>} */
  let drawn = [];
  let cartSubtotal = 0;
  /** @type {Set<string>} */
  let cartVariantIds = new Set();
  /** @type {Array<object>} */
  let cartLines = [];
  /** @type {Map<string, string>} */
  let setNames = new Map();
  /** @type {object | null} */
  let latestCart = null;
  let language = '';
  let catalogReady = false;
  let adding = false;
  let zielTouched = false;
  let amountTimer = 0;

  const formatMoney = (cents) => {
    const fmt = window.formatMoneyDE || window.Kl24EuroFormat?.formatMoneyDE;
    if (fmt) return fmt(cents);
    const value = Math.max(0, Number(cents) || 0) / 100;
    const amount = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `${amount}\u00A0€`;
  };

  const stockWeight = (card) => {
    const qty = Number(card.inventoryQuantity);
    if (!Number.isFinite(qty) || qty <= 0) return 1;
    return Math.min(qty, 24);
  };

  const selectionKey = (cards) =>
    cards
      .map((card) => String(card.variantId))
      .sort()
      .join('|');

  const weightedWindowPick = (windows) => {
    if (!windows.length) return null;
    if (windows.length === 1) return windows[0];
    let total = 0;
    const weights = windows.map((entry) => {
      const closeness = 1 / (1 + entry.over / 10 + entry.dist);
      const stock = entry.cards.reduce((sum, card) => sum + stockWeight(card), 0);
      const weight = closeness * Math.max(stock, 1);
      total += weight;
      return weight;
    });
    let roll = Math.random() * total;
    for (let i = 0; i < windows.length; i += 1) {
      roll -= weights[i];
      if (roll <= 0) return windows[i];
    }
    return windows[windows.length - 1];
  };

  const packSum = (cards) =>
    (cards || []).reduce((total, card) => total + (card.priceCents || 0), 0);

  const withinOvershoot = (sum, gapCents) =>
    sum >= gapCents && sum <= gapCents + OVERSHOOT_CENTS;

  /**
   * Prefer pack that meets target. When both meet Ziel, prefer fewer cards (natural
   * density / faster scrub) then tighter overshoot — never bloat toward MAX_TOTAL.
   */
  const betterPack = (a, b, gapCents) => {
    const sumA = packSum(a);
    const sumB = packSum(b);
    const okA = withinOvershoot(sumA, gapCents);
    const okB = withinOvershoot(sumB, gapCents);
    if (okA !== okB) return okA ? a : b;
    if (okA && okB) {
      const lenA = a?.length || 0;
      const lenB = b?.length || 0;
      if (lenA !== lenB) return lenA < lenB ? a : b;
      return sumA <= sumB ? a : b;
    }
    // Neither in window: climb toward Ziel (higher sum), then more cards as last resort.
    if (sumA !== sumB) return sumA > sumB ? a : b;
    return (a?.length || 0) >= (b?.length || 0) ? a : b;
  };

  /** Drop cheapest extras while sum stays ≥ Ziel — keeps scrub waves small. */
  const trimExcessCards = (cards, gapCents) => {
    if (!strictMeetTarget() || gapCents <= 0 || !cards?.length) return cards || [];
    let working = cards.slice();
    let sum = packSum(working);
    if (!withinOvershoot(sum, gapCents) && sum < gapCents) return working;
    const preferred = Math.min(
      MAX_TOTAL,
      Math.max(40, Math.ceil(gapCents / 33) + 12)
    );
    while (working.length > preferred && sum >= gapCents) {
      let cheapIdx = 0;
      for (let i = 1; i < working.length; i += 1) {
        if ((working[i].priceCents || 0) < (working[cheapIdx].priceCents || 0)) cheapIdx = i;
      }
      const cheap = working[cheapIdx];
      const nextSum = sum - (cheap.priceCents || 0);
      if (nextSum < gapCents) break;
      sum = nextSum;
      working.splice(cheapIdx, 1);
    }
    return working;
  };

  /**
   * Upgrade cheapest → pricier (and light compress) toward gap ≤ sum ≤ gap+OVERSHOOT.
   * Hard caps: ~50ms wall, few iterations. Never returns a pack with lower sum than input
   * (Runde-1 / Remnant-Schutz — kein Mini-Pack-Fallback).
   */
  const densifyToMeetTarget = (cards, pool, gapCents, options = {}) => {
    if (!strictMeetTarget() || gapCents <= 0) return cards || [];
    const baseline = (cards || []).slice();
    const baselineSum = packSum(baseline);
    if (withinOvershoot(baselineSum, gapCents)) return baseline;

    let working = baseline.slice();
    let sum = baselineSum;
    const maxSum = gapCents + OVERSHOOT_CENTS;
    const budgetMs = Math.max(10, Number(options.budgetMs) || 50);
    const maxUpgrades = Math.max(4, Number(options.maxUpgrades) || 40);
    const maxCompress = Math.max(2, Number(options.maxCompress) || 12);
    const t0 = performance.now();
    const timedOut = () => performance.now() - t0 >= budgetMs;

    const used = new Set(working.map((card) => card.variantId));
    // Price-desc pool: find upgrades without scanning every card forever.
    const poolList = (pool || [])
      .filter((card) => card?.variantId && !used.has(card.variantId))
      .sort(
        (a, b) =>
          (b.priceCents || 0) - (a.priceCents || 0) ||
          String(a.variantId).localeCompare(String(b.variantId))
      );

    const apply = (next, nextSum) => {
      if (nextSum <= sum) return false; // never worsen €
      if (nextSum > maxSum) return false;
      if (!holoWithinHalf(next)) return false;
      working = next;
      sum = nextSum;
      used.clear();
      working.forEach((card) => used.add(card.variantId));
      return true;
    };

    // 1:1 upgrades — replace cheapest with a strictly pricier free card.
    for (let guard = 0; guard < maxUpgrades && sum < gapCents && working.length; guard += 1) {
      if (timedOut()) break;
      let cheapIdx = 0;
      for (let i = 1; i < working.length; i += 1) {
        if ((working[i].priceCents || 0) < (working[cheapIdx].priceCents || 0)) cheapIdx = i;
      }
      const cheap = working[cheapIdx];
      const cheapPrice = cheap.priceCents || 0;
      const without = sum - cheapPrice;
      const minPrice = cheapPrice + 1;
      const maxPrice = maxSum - without;
      if (maxPrice < minPrice) break;
      let best = null;
      let bestScore = -1;
      const scanLimit = Math.min(poolList.length, 400);
      for (let i = 0; i < scanLimit; i += 1) {
        const card = poolList[i];
        if (!card.variantId || used.has(card.variantId)) continue;
        const price = card.priceCents || 0;
        if (price < minPrice || price > maxPrice) continue;
        const nextSum = without + price;
        if (nextSum <= sum) continue;
        const trial = working.slice();
        trial[cheapIdx] = card;
        if (!holoWithinHalf(trial)) continue;
        const score =
          nextSum >= gapCents && nextSum <= maxSum
            ? 1_000_000 + (maxSum - nextSum)
            : nextSum;
        if (score > bestScore) {
          bestScore = score;
          best = card;
          if (nextSum >= gapCents && nextSum <= maxSum) break; // good enough, keep CPU low
        }
      }
      if (!best) break;
      if (!apply(working.map((card, idx) => (idx === cheapIdx ? best : card)), without + (best.priceCents || 0))) {
        break;
      }
      if (withinOvershoot(sum, gapCents)) break;
    }

    // Compress only when sum strictly increases (drop 2–4 cheapest, add one better card).
    for (let guard = 0; guard < maxCompress && sum < gapCents && working.length >= 3; guard += 1) {
      if (timedOut()) break;
      const ranked = working
        .map((card, idx) => ({ card, idx, price: card.priceCents || 0 }))
        .sort(
          (a, b) =>
            a.price - b.price || String(a.card.variantId).localeCompare(String(b.card.variantId))
        );
      let moved = false;
      for (let drop = 2; drop <= Math.min(4, ranked.length - 1); drop += 1) {
        const dropSet = new Set(ranked.slice(0, drop).map((entry) => entry.idx));
        const dropIds = new Set(ranked.slice(0, drop).map((entry) => entry.card.variantId));
        const dropSum = ranked.slice(0, drop).reduce((total, entry) => total + entry.price, 0);
        const without = sum - dropSum;
        const maxPrice = maxSum - without;
        const minPrice = dropSum + 1; // must raise €
        if (maxPrice < minPrice) continue;
        let best = null;
        let bestScore = -1;
        const scanLimit = Math.min(poolList.length, 300);
        for (let i = 0; i < scanLimit; i += 1) {
          const card = poolList[i];
          if (!card.variantId || used.has(card.variantId) || dropIds.has(card.variantId)) continue;
          const price = card.priceCents || 0;
          if (price < minPrice || price > maxPrice) continue;
          const nextSum = without + price;
          if (nextSum <= sum) continue;
          const trial = working.filter((_, idx) => !dropSet.has(idx)).concat(card);
          if (!holoWithinHalf(trial)) continue;
          const score =
            nextSum >= gapCents && nextSum <= maxSum
              ? 2_000_000 - Math.abs(nextSum - gapCents)
              : nextSum;
          if (score > bestScore) {
            bestScore = score;
            best = card;
            if (nextSum >= gapCents && nextSum <= maxSum) break;
          }
        }
        if (!best) continue;
        const next = working.filter((_, idx) => !dropSet.has(idx)).concat(best);
        if (!apply(next, without + (best.priceCents || 0))) continue;
        moved = true;
        break;
      }
      if (!moved) break;
      if (withinOvershoot(sum, gapCents)) break;
    }

    // Never return worse than the input pack (fixes 10-Karten / 5,25 € collapse).
    return betterPack(working, baseline, gapCents);
  };

  /**
   * Offline-proven pack builder (harness selectPack): ideal slot price + cheap→pricey swaps.
   * Returns { ok, cards, sum, achievableSum, message }. Deterministic wall typically ≪ 300 ms.
   */
  const selectPackCore = (pool, gapCents, options = {}) => {
    const target = Math.max(0, Number(gapCents) || 0);
    const maxSum = target + OVERSHOOT_CENTS;
    const random = typeof options.random === 'function' ? options.random : Math.random;
    const held = new Set([...(options.heldIds || [])].map(String).filter(Boolean));
    const free = (pool || []).filter(
      (c) => c?.variantId && (c.priceCents || 0) > 0 && !held.has(String(c.variantId))
    );
    if (!free.length || target <= 0) {
      return { ok: false, cards: [], sum: 0, achievableSum: 0, message: 'Kein freier Pool.' };
    }

    const asc = (a, b) =>
      (a.priceCents || 0) - (b.priceCents || 0) ||
      String(a.variantId).localeCompare(String(b.variantId));
    const desc = (a, b) =>
      (b.priceCents || 0) - (a.priceCents || 0) ||
      String(a.variantId).localeCompare(String(b.variantId));
    const lowerBound = (arr, price) => {
      let lo = 0;
      let hi = arr.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if ((arr[mid].priceCents || 0) < price) lo = mid + 1;
        else hi = mid;
      }
      return lo;
    };
    const maxAchievable = () => {
      const normals = free.filter((c) => !isHoloCard(c)).sort(desc);
      const holos = free.filter((c) => isHoloCard(c)).sort(desc);
      let sum = 0;
      let holoSum = 0;
      let n = 0;
      let iN = 0;
      let iH = 0;
      while (n < MAX_TOTAL && (iN < normals.length || iH < holos.length)) {
        const N = normals[iN];
        const H = holos[iH];
        const canH = H && (holoSum + H.priceCents) * 2 <= sum + H.priceCents;
        let pick = null;
        if (N && H) {
          if (!canH || N.priceCents >= H.priceCents) {
            pick = N;
            iN += 1;
          } else {
            pick = H;
            iH += 1;
            holoSum += H.priceCents;
          }
        } else if (N) {
          pick = N;
          iN += 1;
        } else if (H && canH) {
          pick = H;
          iH += 1;
          holoSum += H.priceCents;
        } else break;
        sum += pick.priceCents;
        n += 1;
      }
      return sum;
    };

    const achievableSum = maxAchievable();
    if (achievableSum < target) {
      return {
        ok: false,
        cards: [],
        sum: 0,
        achievableSum,
        message: `Pool reicht nicht: erreichbar ${(achievableSum / 100).toFixed(2)} €, Ziel ${(target / 100).toFixed(2)} €.`,
      };
    }

    const sortedAsc = free.slice().sort(asc);
    const sortedDesc = free.slice().sort(desc);
    // Natural density: ~33 ct @ 39€; slightly higher unit @ 60€+ so scrub stays < 4 s
    // (fewer availability chunks). 250 remains a hard ceiling only.
    const PREFERRED_UNIT_CENTS = target >= 5500 ? 50 : target >= 4000 ? 40 : 33;
    let nCards = Math.min(
      MAX_TOTAL,
      Math.max(
        40,
        Math.ceil(target / PREFERRED_UNIT_CENTS) + Math.floor(random() * 11) - 5
      )
    );

    const used = new Set();
    const working = [];
    let sum = 0;
    const tryAdd = (card) => {
      if (!card || used.has(card.variantId)) return false;
      if (sum + card.priceCents > maxSum) return false;
      if (!holoWithinHalf(working.concat(card))) return false;
      working.push(card);
      used.add(card.variantId);
      sum += card.priceCents;
      return true;
    };

    while (working.length < nCards && sum < target) {
      const slots = nCards - working.length;
      const ideal = Math.max(1, Math.round((target - sum) / slots));
      let idx = lowerBound(sortedAsc, ideal);
      let found = null;
      // Match harness: scan full pool outward from ideal (not a 120-card radius —
      // a short radius left gaps that the backfill then padded to MAX_TOTAL=250).
      for (let d = 0; d < sortedAsc.length; d += 1) {
        const cand = [];
        if (idx + d < sortedAsc.length) cand.push(sortedAsc[idx + d]);
        if (d > 0 && idx - d >= 0) cand.push(sortedAsc[idx - d]);
        for (const card of cand) {
          if (used.has(card.variantId)) continue;
          if (sum + card.priceCents > maxSum) continue;
          if (!holoWithinHalf(working.concat(card))) continue;
          found = card;
          break;
        }
        if (found) break;
      }
      if (!found || !tryAdd(found)) break;
    }

    // Soft backfill only toward nCards (+small slack), never dump up to MAX_TOTAL.
    if (sum < target) {
      const backfillCap = Math.min(MAX_TOTAL, nCards + 25);
      for (const card of sortedAsc) {
        if (working.length >= backfillCap || sum >= target) break;
        tryAdd(card);
      }
    }

    let guard = 0;
    while (sum < target && working.length && guard < 80) {
      guard += 1;
      let cheapIdx = 0;
      for (let i = 1; i < working.length; i += 1) {
        if (working[i].priceCents < working[cheapIdx].priceCents) cheapIdx = i;
      }
      const cheap = working[cheapIdx];
      const without = sum - cheap.priceCents;
      const maxPrice = maxSum - without;
      if (cheap.priceCents + 1 > maxPrice) break;
      const want = Math.min(maxPrice, Math.max(cheap.priceCents + 1, target - without));
      let idx = lowerBound(sortedAsc, want);
      let best = null;
      let bestScore = Infinity;
      for (let d = 0; d < 150; d += 1) {
        const cand = [];
        if (idx + d < sortedAsc.length) cand.push(sortedAsc[idx + d]);
        if (d > 0 && idx - d >= 0) cand.push(sortedAsc[idx - d]);
        for (const card of cand) {
          if (used.has(card.variantId)) continue;
          const p = card.priceCents;
          if (p <= cheap.priceCents || p > maxPrice) continue;
          const nextSum = without + p;
          if (nextSum <= sum) continue;
          const trial = working.slice();
          trial[cheapIdx] = card;
          if (!holoWithinHalf(trial)) continue;
          const score =
            nextSum >= target && nextSum <= maxSum
              ? Math.abs(nextSum - target)
              : 100000 + (target - nextSum);
          if (score < bestScore) {
            bestScore = score;
            best = card;
          }
        }
        if (best && bestScore < 100000) break;
      }
      if (!best) {
        for (const card of sortedDesc) {
          if (used.has(card.variantId)) continue;
          if (card.priceCents <= cheap.priceCents || card.priceCents > maxPrice) continue;
          const trial = working.slice();
          trial[cheapIdx] = card;
          if (!holoWithinHalf(trial)) continue;
          best = card;
          break;
        }
      }
      if (!best) break;
      used.delete(cheap.variantId);
      used.add(best.variantId);
      working[cheapIdx] = best;
      sum = without + best.priceCents;
    }

    const ok = sum >= target && sum <= maxSum && working.length <= MAX_TOTAL && working.length > 0;
    return {
      ok,
      cards: working,
      sum,
      achievableSum,
      message: ok
        ? null
        : sum < target
          ? `Ziel nicht erreicht (erreichbar ${(achievableSum / 100).toFixed(2)} €).`
          : null,
    };
  };

  /**
   * Add cards until sum ≥ gap. Uses ideal slot price; densify only with a tight budget.
   */
  const topUpToMeetTarget = (cards, pool, gapCents, options = {}) => {
    if (!strictMeetTarget() || gapCents <= 0) return cards || [];
    const baseline = (cards || []).slice();
    let working = baseline.slice();
    let sum = packSum(working);
    if (withinOvershoot(sum, gapCents)) return working;
    const used = new Set(working.map((card) => card.variantId));
    const maxSum = gapCents + OVERSHOOT_CENTS;
    const t0 = performance.now();
    const budgetMs = Math.max(10, Number(options.budgetMs) || 50);
    // Asc price for slot fill — stop early when timed out.
    const candidates = (pool || [])
      .filter((card) => card?.variantId && !used.has(card.variantId))
      .sort(
        (a, b) =>
          (a.priceCents || 0) - (b.priceCents || 0) ||
          String(a.variantId).localeCompare(String(b.variantId))
      );

    // Target density from ideal unit — never treat MAX_TOTAL as the planned slot count
    // (that made ideal ≈ 10 ct and padded packs to 250 dimes after scrub).
    const unit = gapCents >= 5500 ? 50 : gapCents >= 4000 ? 40 : 33;
    const targetCount = Math.min(
      MAX_TOTAL,
      Math.max(working.length + 1, Math.ceil(gapCents / unit) + 15)
    );
    const lowerBoundPrice = (arr, price) => {
      let lo = 0;
      let hi = arr.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if ((arr[mid].priceCents || 0) < price) lo = mid + 1;
        else hi = mid;
      }
      return lo;
    };
    while (working.length < MAX_TOTAL && sum < gapCents) {
      if (performance.now() - t0 >= budgetMs) break;
      const slotsPlan = Math.max(1, targetCount - working.length);
      const need = gapCents - sum;
      const ideal = Math.max(
        unit,
        Math.ceil(need / Math.max(1, working.length < targetCount ? slotsPlan : 4))
      );
      const room = maxSum - sum;
      let best = null;
      let bestScore = Infinity;
      // Search outward from ideal price (not from the cheapest end).
      const idx = lowerBoundPrice(candidates, ideal);
      for (let d = 0; d < candidates.length; d += 1) {
        const cand = [];
        if (idx + d < candidates.length) cand.push(candidates[idx + d]);
        if (d > 0 && idx - d >= 0) cand.push(candidates[idx - d]);
        for (const card of cand) {
          if (used.has(card.variantId)) continue;
          const price = card.priceCents || 0;
          if (price <= 0 || price > room) continue;
          const next = working.concat(card);
          if (!holoWithinHalf(next)) continue;
          const nextSum = sum + price;
          const under = Math.max(0, gapCents - nextSum);
          const score = under * 10_000 + Math.abs(price - ideal);
          if (score < bestScore) {
            bestScore = score;
            best = card;
            if (under === 0 && Math.abs(price - ideal) <= unit) break;
          }
        }
        if (best && bestScore < 10_000) break;
      }
      if (!best) break;
      working = working.concat(best);
      used.add(best.variantId);
      sum += best.priceCents || 0;
    }

    if (sum < gapCents) {
      const densifyBudget = Math.max(5, budgetMs - (performance.now() - t0));
      working = densifyToMeetTarget(working, pool, gapCents, {
        budgetMs: densifyBudget,
        maxUpgrades: 40,
        maxCompress: 12,
      });
    }
    return betterPack(working, baseline, gapCents);
  };

  const fillToTarget = (pool, gapCents, anchors = [], options = {}) => {
    if (gapCents <= 0 || !pool.length) return [];
    // Strict free/custom: harness-proven selectPack (ideal unit + upgrades).
    if (strictMeetTarget()) {
      const packed = selectPackCore(pool, gapCents, {
        heldIds: options.heldIds || [],
        random: Math.random,
      });
      try {
        window.__kl24AuffuellerPerf = {
          ...(window.__kl24AuffuellerPerf || {}),
          selectPack: {
            ok: packed.ok,
            sum: packed.sum,
            count: packed.cards?.length || 0,
            achievableSum: packed.achievableSum,
          },
        };
      } catch {
        /* ignore */
      }
      if (packed.ok) {
        return trimExcessCards(packed.cards, gapCents).slice(0, MAX_TOTAL);
      }
      if (packed.achievableSum < gapCents) {
        shortfallReason = 'stock';
        // Pack to the real ceiling (≤ MAX_TOTAL), never the legacy MAX_DRAW=40 window
        // (that produced “40 Karten / 4,00 €” for a ~97 € pool).
        const ceiling = Math.max(1, packed.achievableSum || 0);
        const bestEffort = selectPackCore(pool, ceiling, {
          heldIds: options.heldIds || [],
          random: Math.random,
        });
        if (bestEffort.cards?.length) {
          return betterPack(
            bestEffort.cards,
            maxUnderBudget(pool, ceiling),
            ceiling
          ).slice(0, MAX_TOTAL);
        }
        return maxUnderBudget(pool, ceiling).slice(0, MAX_TOTAL);
      }
      if (packed.cards?.length) {
        return betterPack(
          packed.cards,
          topUpToMeetTarget(packed.cards, pool, gapCents, { budgetMs: 80 }),
          gapCents
        ).slice(0, MAX_TOTAL);
      }
    }
    if (gapCents > LARGE_GAP_CENTS) {
      const combined = combineSets(pool, gapCents, anchors, options);
      if (combined.length) {
        return topUpToMeetTarget(combined, pool, gapCents, { budgetMs: 80 });
      }
    }
    const collectAll = Boolean(options.reroll);
    const take = (windows) => chooseWindow(windows, options)?.cards || [];
    const bySet = groupBySet(pool);
    const anchorSets = new Set(anchors.map((anchor) => anchor.setCode).filter(Boolean));
    const anchorKeys = [...bySet.keys()].filter((key) => anchorSets.has(key));
    const anchorWindows = anchorKeys.flatMap((key) =>
      windowsForSet(bySet, key, gapCents, anchors, collectAll)
    );
    const closeTol = fillToleranceCents();
    const anchorClose = anchorWindows.filter((entry) => entry.over <= closeTol);
    if (anchorClose.length) {
      return topUpToMeetTarget(take(anchorClose), pool, gapCents, { budgetMs: 60 });
    }
    const anchorNear = anchorWindows.filter((entry) => entry.over <= 200);
    if (anchorNear.length) {
      return topUpToMeetTarget(take(anchorNear), pool, gapCents, { budgetMs: 60 });
    }

    const otherKeys = [...bySet.keys()].filter((key) => !anchorSets.has(key));
    const otherWindows = otherKeys.flatMap((key) =>
      windowsForSet(bySet, key, gapCents, anchors, collectAll)
    );
    const otherClose = otherWindows.filter((entry) => entry.over <= closeTol);
    if (otherClose.length) {
      return topUpToMeetTarget(take(otherClose), pool, gapCents, { budgetMs: 60 });
    }
    if (anchorWindows.length) {
      return topUpToMeetTarget(take(anchorWindows), pool, gapCents, { budgetMs: 60 });
    }
    if (otherWindows.length) {
      return topUpToMeetTarget(take(otherWindows), pool, gapCents, { budgetMs: 60 });
    }

    // No consecutive window reaches the gap (pool too small or holo-capped).
    // For free/custom: top up after maxUnderBudget so we still try to reach ≥ Ziel.
    const partial = maxUnderBudget(pool, gapCents);
    return topUpToMeetTarget(partial, pool, gapCents, { budgetMs: 80 });
  };

  /**
   * Best-effort pack when the gap cannot be reached: fill up to MAX_TOTAL under budget/holo.
   * Cap is MAX_TOTAL (not MAX_DRAW) so shortfall messaging reflects real reachable €.
   */
  const maxUnderBudget = (pool, budgetCents) => {
    if (!pool.length || budgetCents <= 0) return [];
    if (pool.length <= MAX_TOTAL) {
      const allSum = pool.reduce((sum, card) => sum + card.priceCents, 0);
      if (allSum <= budgetCents + TOLERANCE_CENTS && holoWithinHalf(pool)) return [...pool];
    }
    const normals = pool
      .filter((card) => !isHoloCard(card))
      .sort(
        (a, b) =>
          a.priceCents - b.priceCents ||
          cardSortNumber(a) - cardSortNumber(b) ||
          String(a.variantId).localeCompare(String(b.variantId))
      );
    const holos = pool
      .filter((card) => isHoloCard(card))
      .sort(
        (a, b) =>
          b.priceCents - a.priceCents ||
          cardSortNumber(a) - cardSortNumber(b) ||
          String(a.variantId).localeCompare(String(b.variantId))
      );
    const picked = [];
    let sum = 0;
    let holoSum = 0;
    for (const card of normals) {
      if (picked.length >= MAX_TOTAL) break;
      if (sum + card.priceCents > budgetCents + TOLERANCE_CENTS) continue;
      picked.push(card);
      sum += card.priceCents;
    }
    for (const card of holos) {
      if (picked.length >= MAX_TOTAL) break;
      const price = card.priceCents;
      if (sum + price > budgetCents + TOLERANCE_CENTS) continue;
      const nextHolo = holoSum + price;
      const nextSum = sum + price;
      if (capHoloShare && nextHolo * 2 > nextSum) continue;
      picked.push(card);
      sum = nextSum;
      holoSum = nextHolo;
    }
    if (picked.length) return picked;
    const sorted = [...pool].sort(
      (a, b) =>
        cardSortNumber(a) - cardSortNumber(b) || String(a.variantId).localeCompare(String(b.variantId))
    );
    return bestPartial(sorted, budgetCents, [])?.cards || [];
  };

  const cardSortNumber = (card) => {
    const match = /^(\d+)/.exec(String(card?.cardNumber || ''));
    return match ? Number.parseInt(match[1], 10) : 99999;
  };

  const windowDistance = (startNum, endNum, anchorNumbers) => {
    if (!anchorNumbers.length) return 0;
    const low = Math.min(...anchorNumbers);
    const high = Math.max(...anchorNumbers);
    if (endNum >= low && startNum <= high) return 0;
    if (endNum < low) return low - endNum;
    return startNum - high;
  };

  let capHoloShare = true;
  let shortfallReason = '';
  let drawToken = 0;
  /** True while a draw is in flight (fill; scrub may continue afterward). */
  let drawing = false;
  /** True until availability scrub finished for the current draw — blocks Add. */
  let scrubPending = false;
  /** After add-time scrub changed the pack: show update, require a second click. */
  let awaitAddConfirm = false;
  let displayedSelectionKey = '';
  let displayedSumCents = 0;
  let drawIdleHandle = null;
  let drawTimeoutHandle = null;
  /** Bumped to cancel in-flight ensureReservableCards (preview scrub vs addAll). */
  let reserveGen = 0;
  const addBtnDefaultLabel = (addBtn?.textContent || 'Alle in den Warenkorb').trim();

  const isHoloCard = (card) => card?.finish === 'Holo' || card?.finish === 'Reverse Holo';

  const holoWithinHalf = (cards) => {
    if (!capHoloShare) return true;
    let total = 0;
    let holo = 0;
    cards.forEach((card) => {
      total += card.priceCents;
      if (isHoloCard(card)) holo += card.priceCents;
    });
    return holo * 2 <= total;
  };

  const listWindows = (sorted, gapCents, anchorNumbers, collectAll = false) => {
    const windows = [];
    for (let i = 0; i < sorted.length; i += 1) {
      let sum = 0;
      for (let j = i; j < sorted.length && j - i + 1 <= MAX_DRAW; j += 1) {
        sum += sorted[j].priceCents;
        if (sum < gapCents) continue;
        const over = sum - gapCents;
        const cards = sorted.slice(i, j + 1);
        if (!holoWithinHalf(cards)) continue;
        windows.push({
          cards,
          over,
          dist: windowDistance(cardSortNumber(sorted[i]), cardSortNumber(sorted[j]), anchorNumbers),
        });
        if (!collectAll || over > TOLERANCE_CENTS) break;
      }
    }
    return windows;
  };

  const chooseWindow = (windows, options = {}) => {
    if (!windows.length) return null;
    const inTolerance = windows.filter((entry) => entry.over <= TOLERANCE_CENTS);
    const candidates = inTolerance.length ? inTolerance : windows;
    const bestDist = candidates.reduce((min, entry) => Math.min(min, entry.dist), Infinity);
    if (!options.reroll) {
      const near = candidates.filter((entry) => entry.dist <= bestDist + 8);
      const bestOver = near.reduce((min, entry) => Math.min(min, entry.over), Infinity);
      const tight = near.filter((entry) => entry.over <= bestOver + 10);
      return tight[Math.floor(Math.random() * tight.length)] || null;
    }
    const generous = candidates.filter((entry) => entry.dist <= bestDist + 30);
    let pool = generous.length ? generous : candidates;
    if (options.avoidKey) {
      const different = pool.filter((entry) => selectionKey(entry.cards) !== options.avoidKey);
      if (different.length) pool = different;
    }
    return weightedWindowPick(pool);
  };

  const groupBySet = (pool) => {
    const bySet = new Map();
    pool.forEach((card) => {
      const key = card.setCode || '';
      if (!bySet.has(key)) bySet.set(key, []);
      bySet.get(key).push(card);
    });
    bySet.forEach((list) => {
      list.sort(
        (a, b) => cardSortNumber(a) - cardSortNumber(b) || String(a.variantId).localeCompare(String(b.variantId))
      );
    });
    return bySet;
  };

  const windowsForSet = (bySet, key, gapCents, anchors, collectAll = false) => {
    const numbers = anchors.filter((anchor) => anchor.setCode === key).map((anchor) => anchor.number);
    return listWindows(bySet.get(key) || [], gapCents, numbers, collectAll);
  };

  const varyDrawByReplacement = (cards, pool, gapCents) => {
    if (!cards.length || gapCents <= 0) return null;
    const originalKey = selectionKey(cards);
    for (let index = 0; index < cards.length; index += 1) {
      const card = cards[index];
      const blocked = new Set(cards.map((entry) => entry.variantId));
      for (let attempt = 0; attempt < 6; attempt += 1) {
        const next = replacementFor(card, pool, blocked);
        if (!next) break;
        blocked.add(next.variantId);
        const replaced = cards.map((entry, entryIndex) => (entryIndex === index ? next : entry));
        const sum = replaced.reduce((total, entry) => total + entry.priceCents, 0);
        if (sum < gapCents || sum - gapCents > (strictMeetTarget() ? OVERSHOOT_CENTS : TOLERANCE_CENTS))
          continue;
        if (!holoWithinHalf(replaced)) continue;
        if (selectionKey(replaced) === originalKey) continue;
        return replaced;
      }
    }
    return null;
  };

  const bestPartial = (sorted, budgetCents, anchorNumbers) => {
    let best = null;
    for (let i = 0; i < sorted.length; i += 1) {
      let sum = 0;
      for (let j = i; j < sorted.length && j - i + 1 <= MAX_DRAW; j += 1) {
        sum += sorted[j].priceCents;
        if (sum > budgetCents + TOLERANCE_CENTS) break;
        if (!holoWithinHalf(sorted.slice(i, j + 1))) continue;
        const dist = windowDistance(cardSortNumber(sorted[i]), cardSortNumber(sorted[j]), anchorNumbers);
        if (!best || sum > best.sum || (sum === best.sum && dist < best.dist)) {
          best = { cards: sorted.slice(i, j + 1), sum, dist };
        }
      }
    }
    return best;
  };

  const filtersActive = () => {
    if (selectedSetCodes().size > 0) return true;
    if (energySelect?.value) return true;
    if (typeSelect?.value) return true;
    if (holoInput?.checked) return true;
    if (commonsInput?.checked) return true;
    if (cartSetInput?.checked) return true;
    if (maxPriceCents() > 0) return true;
    if (language) return true;
    return false;
  };

  const markShortfall = (pool, gapCents, achieved, cardCount) => {
    if (achieved >= gapCents) {
      shortfallReason = '';
      return;
    }
    let nonHolo = 0;
    let all = 0;
    pool.forEach((card) => {
      all += card.priceCents;
      if (!isHoloCard(card)) nonHolo += card.priceCents;
    });
    const holoPool = all - nonHolo;
    const underCap = nonHolo + Math.min(holoPool, nonHolo);
    const ceiling = Math.min(all, gapCents);
    // Prefer holo explanation when the cap (not mere stock) trimmed the reachable sum.
    if (capHoloShare && underCap < ceiling && achieved <= underCap + TOLERANCE_CENTS) {
      shortfallReason = 'holo';
    } else if (all < gapCents) {
      shortfallReason = 'stock';
    } else if (capHoloShare && underCap < gapCents) {
      shortfallReason = 'holo';
    } else if (cardCount >= MAX_TOTAL) {
      shortfallReason = 'limit';
    } else if (all < gapCents) {
      shortfallReason = 'stock';
    } else {
      // Pool could reach target but composition/reservations left a gap.
      shortfallReason = 'partial';
    }
  };

  const shortfallSummary = (cents) => {
    const filtered = filtersActive();
    const maxLabel = formatMoney(cents);
    const base = filtered
      ? `Aktuell maximal ${maxLabel} mit diesen Filtern`
      : `Aktuell maximal ${maxLabel}`;
    if (shortfallReason === 'holo') {
      return `${base} – Holo-Regel (höchstens die Hälfte). „Nur Holo“ erlaubt mehr Holos.`;
    }
    if (shortfallReason === 'limit') {
      return `${base} – Kartenlimit ${MAX_TOTAL}`;
    }
    if (shortfallReason === 'partial') {
      return filtered
        ? `${base} – bitte Filter lockern oder neue Auswahl laden.`
        : `${base} – bitte neue Auswahl laden oder etwas später erneut versuchen.`;
    }
    return filtered
      ? `${base} – Bestand reicht nicht für dein Ziel.`
      : `${base} – freier Bestand reicht gerade nicht für dein Ziel.`;
  };

  /** Gratis-Versand + Eigener Betrag: Summe muss ≥ Ziel (keine Unter-Toleranz). */
  const strictMeetTarget = () => {
    const m = mode();
    return m === 'free' || m === 'custom';
  };

  /** Tolerance under target, but never below store minimum (checkout lock). */
  const allowedUnderTarget = (target) => {
    if (target == null) return 0;
    if (strictMeetTarget()) return 0;
    if (target <= rules.minOrderCents) return 0;
    return Math.min(TOLERANCE_CENTS, Math.max(0, target - rules.minOrderCents));
  };

  const fillToleranceCents = () => (strictMeetTarget() ? 0 : TOLERANCE_CENTS);

  const compactReserveNotes = (notes) => {
    const list = (notes || []).filter(Boolean);
    if (!list.length) return '';
    if (list.length <= 2) return list.join(' ');
    return `${list.length} Karten wurden wegen Reservierung oder Bestand ersetzt.`;
  };

  const combineSets = (pool, gapCents, anchors, options) =>
    window.Kl24SinglesPool.pickForGap(pool, gapCents, {
      ...(options || {}),
      anchors,
      capHoloShare,
      maxTotal: MAX_TOTAL,
      // Free/custom: allow overshoot so pickForGap can cross the target (never stop short as "ok").
      toleranceCents: strictMeetTarget() ? OVERSHOOT_CENTS : TOLERANCE_CENTS,
    });

  const replacementFor = (card, pool, blockedIds) =>
    window.Kl24SinglesPool.replacementFor(card, pool, blockedIds);

  const tagList = (product) => {
    if (Array.isArray(product.tags)) return product.tags;
    if (typeof product.tags === 'string') {
      return product.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = String(html || '');
    return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
  };

  const parseFromSku = (sku) => {
    const raw = String(sku || '').trim().toUpperCase();
    if (!raw) return null;
    let match = PM_SKU_FULL.exec(raw);
    if (match) {
      return {
        setCode: match[1],
        cardNumber: `${Number.parseInt(match[2], 10)}/${match[3]}`,
        condition: match[4],
      };
    }
    match = PM_SKU_SHORT.exec(raw);
    if (match) {
      return { setCode: match[1], cardNumber: String(Number.parseInt(match[2], 10)), condition: match[3] };
    }
    match = POK_SKU.exec(raw);
    if (match) {
      return { setCode: match[1], cardNumber: String(Number.parseInt(match[2], 10)), condition: match[3] };
    }
    return null;
  };

  const parseCardNumberFromTitle = (title) => {
    const match = CARD_NUM_RE.exec(String(title || ''));
    if (!match) return '';
    return `${Number.parseInt(match[1], 10)}/${match[2]}`;
  };

  const parseLanguage = (tags, sku, title) => {
    const lower = tags.map((tag) => String(tag).toLowerCase().trim());
    if (lower.some((tag) => tag === 'deutsch' || tag === 'german' || tag === 'de')) return 'Deutsch';
    if (lower.some((tag) => tag === 'englisch' || tag === 'english' || tag === 'en')) return 'Englisch';
    if (String(sku || '').toUpperCase().endsWith('-EN')) return 'Englisch';
    if (/\(englisch\)|\(english\)/i.test(String(title || ''))) return 'Englisch';
    return '';
  };

  const parseFinish = (tags, sku) => {
    const raw = String(sku || '').trim().toUpperCase().replace(/-EN$/, '');
    if (raw.endsWith('-RH')) return 'Reverse Holo';
    if (raw.endsWith('-H')) return 'Holo';
    const lower = tags.map((tag) => String(tag).toLowerCase().trim());
    if (lower.some((tag) => tag.includes('reverse'))) return 'Reverse Holo';
    if (lower.some((tag) => tag.includes('holo') || tag.includes('foil') || tag === 'glitzer')) return 'Holo';
    return '';
  };

  const parseRarity = (tags) => {
    const lower = tags.map((tag) => String(tag).toLowerCase().trim());
    if (lower.some((tag) => tag === 'common' || tag === 'gewöhnlich' || tag === 'gewoehnlich')) return 'common';
    return '';
  };

  const parseEnergy = (tags, bodyHtml) => {
    const lower = tags.map((tag) => String(tag).toLowerCase().trim());
    for (const energy of ENERGY_TYPES) {
      if (lower.some((tag) => tag === energy || tag.includes(energy))) return ENERGY_LABELS[energy];
    }
    const plain = stripHtml(bodyHtml);
    if (!plain) return '';
    for (const segment of plain.split('·').map((part) => part.trim().toLowerCase())) {
      if (ENERGY_LABELS[segment]) return ENERGY_LABELS[segment];
      if (ENERGY_EN_TO_DE[segment]) return ENERGY_EN_TO_DE[segment];
    }
    return '';
  };

  const stripTrailingParen = (text) => {
    const raw = String(text || '').trim();
    if (!raw.endsWith(')')) return raw;
    let depth = 0;
    for (let i = raw.length - 1; i >= 0; i -= 1) {
      if (raw[i] === ')') depth += 1;
      else if (raw[i] === '(') {
        depth -= 1;
        if (depth === 0) return raw.slice(0, i).trim();
      }
    }
    return raw;
  };

  const CONDITION_LABELS = {
    NM: 'Near Mint',
    EX: 'Excellent',
    LP: 'Lightly Played',
    MP: 'Moderately Played',
    DMG: 'Damaged',
  };

  const knownCondition = (code) => {
    const upper = String(code || '').toUpperCase();
    return CONDITION_LABELS[upper] ? upper : '';
  };

  const parseCondition = (title, skuCondition) => {
    const fromSku = knownCondition(skuCondition);
    if (fromSku) return fromSku;
    const match = /\((NM|EX|LP|MP|DMG)\)/i.exec(String(title || ''));
    return match ? match[1].toUpperCase() : '';
  };

  const cardNameFromTitle = (title, cardNumber) => {
    let display = String(title || '').trim();
    display = display
      .replace(/\s*\((englisch|english|deutsch|german)\)/gi, '')
      .replace(/\s*\((NM|EX|LP|MP|DMG)\)/gi, '')
      .trim();
    display = stripTrailingParen(display);
    display = display.replace(/\s*[–-]\s*\d+\s*\/\s*\d+\s*$/u, '').trim();
    if (cardNumber) {
      const pattern = new RegExp(`\\s*[–-]\\s*${cardNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`);
      display = display.replace(pattern, '').trim();
    }
    return display || String(title || '').trim();
  };

  const ownPhoto = (url) => {
    const raw = String(url || '').trim();
    const lower = raw.toLowerCase();
    if (!lower || lower.includes('tcgdex.net')) return '';
    return lower.includes('cdn.shopify.com') ? raw : '';
  };

  const thumbUrl = (url) => {
    const api = window.Kl24ShopifyImage;
    if (api?.sizedUrl) return api.sizedUrl(url, api.WIDTHS?.gridThumb || 480);
    return url;
  };

  const normalizeProduct = (product) => {
    const tags = tagList(product);
    const variants = product.variants || [];
    const variant =
      variants.find((entry) => entry.available && Number.parseFloat(entry.price || '0') > 0) ||
      variants[0] ||
      {};
    const sku = String(variant.sku || '').trim();
    const parsed = parseFromSku(sku) || {};
    const cardNumber = parsed.cardNumber || parseCardNumberFromTitle(product.title) || '';
    const priceCents = Math.round(Number.parseFloat(variant.price || '0') * 100);
    const setCode = String(parsed.setCode || '').trim().toUpperCase();
    const imageUrl = ownPhoto((product.images || [])[0]?.src || product.image?.src || '');
    return {
      variantId: variant.id != null ? String(variant.id) : '',
      handle: product.handle,
      title: product.title,
      cardName: cardNameFromTitle(product.title, cardNumber),
      condition: parseCondition(product.title, parsed.condition),
      cardNumber,
      setCode,
      setName: '',
      language: parseLanguage(tags, sku, product.title),
      energyType: parseEnergy(tags, product.body_html),
      finish: parseFinish(tags, sku),
      rarity: parseRarity(tags),
      productType: String(product.product_type || '').trim(),
      priceCents,
      available: Boolean(variant.available),
      inventoryQuantity: Number.isFinite(Number(variant.inventory_quantity))
        ? Number(variant.inventory_quantity)
        : null,
      sku,
      imageUrl,
    };
  };

  const applySetNames = () => {
    rebuildSkuAliases(setIndex);
    const byCode = new Map();
    setIndex.forEach((entry) => {
      const code = normalizeSetCode(entry.set_code || entry.handle);
      const name = String(entry.name || '').trim();
      if (code && name) byCode.set(code, name);
    });
    Object.entries(DISPLAY_NAME_FALLBACKS).forEach(([code, name]) => {
      const canonical = normalizeSetCode(code);
      if (canonical && name && !byCode.has(canonical)) byCode.set(canonical, name);
    });
    setNames = byCode;
    catalog.forEach((card) => {
      card.setCode = normalizeSetCode(card.setCode);
      card.setName = byCode.get(card.setCode) || card.setCode;
    });
  };

  const readCache = () => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (!parsed || parsed.handle !== collectionHandle) return null;
      if (Date.now() - Number(parsed.savedAt) > CACHE_TTL_MS) return null;
      if (!Array.isArray(parsed.products) || !parsed.products.length) return null;
      return parsed.products;
    } catch {
      return null;
    }
  };

  const writeCache = (products) => {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ handle: collectionHandle, savedAt: Date.now(), products })
      );
    } catch {
      /* private mode or quota */
    }
  };

  const isSingleListing = (product) => {
    const tags = tagList(product).map((tag) => String(tag).trim().toLowerCase());
    if (tags.some((tag) => tag === 'einzelkarte' || tag === 'pokemon-single')) return true;
    return String(product.product_type || '').toLowerCase().includes('einzelkarte');
  };

  const fetchCollectionProducts = async (handle) => {
    const items = [];
    for (let page = 1; page <= 20; page += 1) {
      const res = await fetch(
        `${window.location.origin}/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`
      );
      if (!res.ok) break;
      const data = await res.json();
      const batch = data.products || [];
      if (!batch.length) break;
      items.push(...batch);
      if (batch.length < 250) break;
    }
    return items;
  };

  const loadRawProducts = async () => {
    const handles = [...new Set([collectionHandle, 'pokemon-tcg'].map((handle) => String(handle || '').trim()).filter(Boolean))];
    for (const handle of handles) {
      const products = await fetchCollectionProducts(handle);
      if (!products.length) continue;
      const singles = products.filter(isSingleListing);
      return singles.length ? singles : products;
    }
    return [];
  };

  const loadSetIndex = async () => {
    const url = root.dataset.setsIndex;
    if (!url) return [];
    try {
      const res = await fetch(url);
      if (!res.ok) return [];
      const data = await res.json();
      return data.sets || [];
    } catch {
      return [];
    }
  };

  const urlSetTokens = () => {
    const params = new URLSearchParams(window.location.search);
    const tokens = [];
    params.getAll('set').forEach((part) => {
      String(part)
        .split(',')
        .forEach((bit) => {
          const value = bit.trim().toLowerCase();
          if (value) tokens.push(value);
        });
    });
    return tokens;
  };

  const resolveUrlSetCodes = () => {
    const codes = new Set();
    urlSetTokens().forEach((token) => {
      const hit = setIndex.find(
        (entry) =>
          String(entry.handle || '').toLowerCase() === token ||
          String(entry.set_code || '').toLowerCase() === token
      );
      codes.add(normalizeSetCode(hit ? hit.set_code || hit.handle : token));
    });
    return codes;
  };

  const selectedSetCodes = () => {
    const codes = new Set();
    setsEl?.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      if (input.value) codes.add(input.value);
    });
    return codes;
  };

  const mode = () => root.querySelector('[data-fill-mode]:checked')?.value || 'free';

  const targetCents = () => {
    if (mode() === 'minimum') return rules.minOrderCents;
    if (mode() === 'free') return rules.freeShippingCents;
    const euros = Number(String(amountInput?.value || '').replace(',', '.'));
    if (!Number.isFinite(euros) || euros <= 0) return null;
    return Math.round(euros * 100);
  };

  const wishExtraCents = () =>
    wishCards
      .filter((card) => !cartVariantIds.has(card.variantId))
      .reduce((sum, card) => sum + card.priceCents, 0);

  const startingCents = () => cartSubtotal + wishExtraCents();

  const maxPriceCents = () => {
    const raw = root.querySelector('[data-fill-max].is-active')?.getAttribute('data-fill-max') || '';
    const cents = Number(raw);
    return Number.isFinite(cents) && cents > 0 ? cents : 0;
  };

  const cartSetCodes = () => {
    const codes = new Set();
    cartLines.forEach((card) => {
      const code = normalizeSetCode(card.setCode);
      if (code) codes.add(code);
    });
    return codes;
  };

  const filteredPool = () => {
    const sets = selectedSetCodes();
    const energy = energySelect?.value || '';
    const type = typeSelect?.value || '';
    const holoOnly = Boolean(holoInput?.checked);
    const commonsOnly = Boolean(commonsInput?.checked);
    const cartSetOnly = Boolean(cartSetInput?.checked);
    const priceCap = maxPriceCents();
    const cartSets = cartSetCodes();
    const excludeCart = excludeCartInput?.checked !== false;
    const blocked = new Set(wishCards.map((card) => card.variantId));
    if (excludeCart) cartVariantIds.forEach((id) => blocked.add(id));
    return window.Kl24SinglesPool.filter(catalog, {
      blockedIds: blocked,
      maxPriceCents: priceCap,
      language,
      cartSetOnly,
      cartSets,
      setCodes: sets,
      energy,
      productType: type,
      holoOnly,
      commonsOnly,
    });
  };

  const showFeedback = (message, type) => {
    if (!feedbackEl) return;
    feedbackEl.hidden = !message;
    feedbackEl.textContent = message || '';
    feedbackEl.classList.toggle('is-success', type === 'success');
    feedbackEl.classList.toggle('is-error', type === 'error');
  };

  const renderStart = () => {
    if (!startEl) return;
    const wish = wishExtraCents();
    const parts = [`Warenkorb ${formatMoney(cartSubtotal)}`];
    if (wish) parts.push(`Wunschkarten ${formatMoney(wish)}`);
    parts.push(`Ausgang ${formatMoney(startingCents())}`);
    startEl.textContent = parts.join(' · ');
  };

  const metaLine = (card) => {
    const bits = [card.setName || card.setCode, card.cardNumber, card.language, card.finish].filter(Boolean);
    return bits.join(' · ');
  };

  const conditionPill = (card) => {
    const code = String(card?.condition || '').toUpperCase();
    const label = CONDITION_LABELS[code];
    if (!label) return null;
    const pill = document.createElement('span');
    pill.className = `karten-auffueller__condition karten-auffueller__condition--${code.toLowerCase()}`;
    pill.title = label;
    pill.setAttribute('aria-label', `Zustand: ${label} (${code})`);
    const mark = document.createElement('span');
    mark.className = 'karten-auffueller__condition-code';
    mark.textContent = code;
    mark.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.className = 'karten-auffueller__condition-label';
    text.textContent = label;
    pill.append(mark, text);
    return pill;
  };

  let zoomEl = null;
  let zoomAnchor = null;

  const previewUrl = (url) => {
    const api = window.Kl24ShopifyImage;
    if (api?.sizedUrl) return api.sizedUrl(url, api.WIDTHS?.cardPreview || 600);
    return url;
  };

  const hideZoom = () => {
    zoomAnchor = null;
    if (!zoomEl) return;
    zoomEl.hidden = true;
  };

  const placeZoom = (anchor) => {
    if (!zoomEl || zoomEl.hidden) return;
    const box = anchor.getBoundingClientRect();
    const width = Math.min(280, window.innerWidth - 24);
    const height = Math.round(width * (88 / 63)) + 8;
    let left = box.right + 12;
    if (left + width > window.innerWidth - 12) left = box.left - width - 12;
    if (left < 12) left = Math.max(12, (window.innerWidth - width) / 2);
    let top = box.top;
    if (top + height > window.innerHeight - 12) top = Math.max(12, window.innerHeight - height - 12);
    zoomEl.style.width = `${width}px`;
    zoomEl.style.left = `${left}px`;
    zoomEl.style.top = `${top}px`;
  };

  const showZoom = (anchor, card) => {
    if (!card?.imageUrl) return;
    if (!zoomEl) {
      zoomEl = document.createElement('div');
      zoomEl.className = 'karten-auffueller__zoom';
      zoomEl.hidden = true;
      const img = document.createElement('img');
      img.alt = '';
      zoomEl.append(img);
      document.body.append(zoomEl);
      window.addEventListener('scroll', hideZoom, true);
      window.addEventListener('resize', hideZoom);
    }
    const img = zoomEl.querySelector('img');
    img.alt = card.cardName || '';
    if (img.getAttribute('src') !== previewUrl(card.imageUrl)) img.src = previewUrl(card.imageUrl);
    zoomEl.hidden = false;
    zoomAnchor = anchor;
    placeZoom(anchor);
  };

  const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const mountPhoto = (card, alt) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'karten-auffueller__photo';
    const img = document.createElement('img');
    img.alt = alt || '';
    if (card.imageUrl) {
      img.src = thumbUrl(card.imageUrl);
      button.setAttribute('aria-label', `${alt || card.cardName || 'Karte'} vergrößern`);
      button.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        showZoom(button, card);
      });
      button.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        hideZoom();
      });
      button.addEventListener('focus', () => showZoom(button, card));
      button.addEventListener('blur', () => hideZoom());
      button.addEventListener('click', () => {
        if (finePointer()) return;
        if (zoomAnchor === button && zoomEl && !zoomEl.hidden) hideZoom();
        else showZoom(button, card);
      });
    } else {
      img.hidden = true;
      button.disabled = true;
    }
    button.append(img);
    return button;
  };

  const nameRow = (card, className) => {
    const row = document.createElement('div');
    row.className = 'karten-auffueller__name-row';
    const name = document.createElement('p');
    name.className = className;
    name.textContent = card.cardName;
    row.append(name);
    const pill = conditionPill(card);
    if (pill) row.append(pill);
    return row;
  };

  const renderWishes = () => {
    if (!wishPicked) return;
    wishPicked.replaceChildren();
    wishCards.forEach((card) => {
      const item = document.createElement('li');
      item.className = 'kl24-builder__card';
      const paint = energyPaint(card.energyType);
      item.style.setProperty('--energy', paint.color);
      const body = document.createElement('div');
      body.className = 'kl24-builder__copy';
      const meta = document.createElement('p');
      meta.className = 'kl24-builder__set';
      meta.textContent = [card.setName || card.setCode, card.cardNumber].filter(Boolean).join(' · ');
      const price = document.createElement('p');
      price.className = 'kl24-builder__price';
      price.textContent = formatMoney(card.priceCents);
      body.append(nameRow(card, 'kl24-builder__name'), meta);
      const actions = document.createElement('div');
      actions.className = 'kl24-builder__actions';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'kl24-builder__icon-btn';
      remove.setAttribute('aria-label', 'Entfernen');
      remove.textContent = '×';
      remove.addEventListener('click', () => {
        wishCards = wishCards.filter((entry) => entry.variantId !== card.variantId);
        renderWishes();
        requestDraw();
      });
      actions.append(remove);
      const photo = mountPhoto(card, '');
      photo.classList.add('kl24-builder__thumb');
      item.append(photo, body, price, actions);
      wishPicked.append(item);
    });
  };

  const setNameFor = (code) => {
    const canonical = normalizeSetCode(code);
    return setNames.get(canonical) || canonical;
  };

  const cardFromCartItem = (item) => {
    const variantId = item?.variant_id != null ? String(item.variant_id) : '';
    const known = catalog.find((card) => card.variantId === variantId);
    if (known) return known;
    const parsed = parseFromSku(item?.sku) || {};
    const setCode = normalizeSetCode(parsed.setCode || '');
    const title = item?.product_title || item?.title || '';
    const cardNumber = parsed.cardNumber || parseCardNumberFromTitle(title) || '';
    const imageValue = typeof item?.image === 'string' ? item.image : item?.image?.src || '';
    return {
      variantId,
      handle: item?.handle || '',
      title,
      cardName: cardNameFromTitle(title, cardNumber),
      condition: parseCondition(title, parsed.condition),
      cardNumber,
      setCode,
      setName: setNameFor(setCode),
      language: parseLanguage([], item?.sku, title),
      finish: parseFinish([], item?.sku),
      priceCents: Number(item?.line_price) || Number(item?.price) || 0,
      imageUrl: ownPhoto(imageValue) || '',
      sku: String(item?.sku || ''),
      available: true,
    };
  };

  const applyCart = (cart) => {
    if (!cart) return;
    latestCart = cart;
    cartSubtotal = Number(cart.items_subtotal_price) || 0;
    cartVariantIds = new Set((cart.items || []).map((item) => String(item.variant_id)));
    cartLines = (cart.items || []).map(cardFromCartItem).filter((card) => card.variantId);
  };

  const collectAnchors = () => {
    const anchors = [];
    const push = (setCode, cardNumber) => {
      const code = normalizeSetCode(setCode);
      const number = cardSortNumber({ cardNumber });
      if (!code || number === 99999) return;
      anchors.push({ setCode: code, number });
    };
    cartLines.forEach((card) => push(card.setCode, card.cardNumber));
    wishCards.forEach((card) => {
      if (!cartVariantIds.has(card.variantId)) push(card.setCode, card.cardNumber);
    });
    return anchors;
  };

  const previewRows = () => {
    const rows = cartLines.map((card) => ({ ...card, role: 'cart' }));
    wishCards.forEach((card) => {
      if (cartVariantIds.has(card.variantId)) return;
      rows.push({ ...card, role: 'wish' });
    });
    drawn.forEach((card) => rows.push({ ...card, role: 'fill' }));
    const roleOrder = { cart: 0, wish: 1, fill: 2 };
    rows.sort((a, b) => {
      const bySet = String(a.setCode || '').localeCompare(String(b.setCode || ''), 'de');
      if (bySet) return bySet;
      const byNumber = cardSortNumber(a) - cardSortNumber(b);
      if (byNumber) return byNumber;
      return (roleOrder[a.role] || 0) - (roleOrder[b.role] || 0);
    });
    return rows;
  };

  const renderPreview = () => {
    renderStart();
    const target = targetCents();
    const pool = catalogReady ? filteredPool() : [];
    const drawnSum = drawn.reduce((sum, card) => sum + card.priceCents, 0);
    const total = startingCents() + drawnSum;
    const poolSum = pool.reduce((sum, card) => sum + card.priceCents, 0);
    const reachable = startingCents() + poolSum;
    // gap must be function-scoped: markShortfall below used to read a block-local `gap`
    // (minifiers may rename it gap2) → ReferenceError under shortfall / reservation load.
    const gap = target == null ? 0 : target - startingCents();
    if (drawing && target != null && summaryEl) {
      summaryEl.textContent = 'Wird berechnet …';
      summaryEl.classList.remove('is-error', 'is-success');
    }
    if (poolEl) {
      const fillSets = new Set(drawn.map((card) => card.setCode).filter(Boolean));
      const largeGap = gap > LARGE_GAP_CENTS;
      const usedConsecutive = drawn.length > 0 && gap > 0 && drawnSum >= gap;
      const cluster =
        drawn.length === 0
          ? ''
          : largeGap
            ? ` · Aufgefüllt aus ${fillSets.size === 1 ? '1 Set' : `${fillSets.size} Sets`}, teurere Karten zuerst`
            : usedConsecutive && fillSets.size <= 1
              ? ' · Aufgefüllt aus 1 Set, hintereinander'
              : usedConsecutive
                ? ` · Aufgefüllt aus ${fillSets.size} Sets, jeweils hintereinander`
                : fillSets.size <= 1
                  ? ' · Bestmögliche Auswahl aus 1 Set'
                  : ` · Bestmögliche Auswahl aus ${fillSets.size} Sets`;
      if (!catalogReady) {
        poolEl.textContent = '';
      } else if (drawn.length) {
        let line = `${drawn.length} Karten · ${formatMoney(drawnSum)} gehen in den Warenkorb`;
        if (pool.length > drawn.length) {
          line +=
            shortfallReason === 'holo'
              ? ` (${pool.length} passen, Holo-Regel begrenzt)`
              : ` (${pool.length} passen zu den Filtern)`;
        }
        poolEl.textContent = `${line}${cluster}`;
      } else {
        poolEl.textContent = `${pool.length} Karten passen zu den Filtern${cluster}`;
      }
    }
    const barCountEl = root.querySelector('[data-fill-bar-count]');
    const barSumEl = root.querySelector('[data-fill-bar-sum]');
    if (barCountEl) barCountEl.textContent = drawn.length === 1 ? '1 Karte' : `${drawn.length} Karten`;
    if (barSumEl) barSumEl.textContent = formatMoney(drawnSum);
    if (summaryEl) {
      let summaryTone = 'error';
      if (drawing && target != null) {
        summaryEl.textContent = 'Wird berechnet …';
        summaryTone = '';
      } else if (target == null) {
        summaryEl.textContent = 'Gib einen Betrag ein.';
      } else if (startingCents() >= target && !drawn.length) {
        summaryEl.textContent = `Ziel schon erreicht (${formatMoney(startingCents())} von ${formatMoney(target)}).`;
        summaryTone = 'success';
      } else if (drawn.length) {
        const delta = total - target;
        const underOk = allowedUnderTarget(target);
        if (delta < -underOk || (total < rules.minOrderCents && target <= rules.minOrderCents)) {
          if (!shortfallReason && delta < -underOk) markShortfall(pool, gap, drawnSum, drawn.length);
          summaryEl.textContent = shortfallReason
            ? shortfallSummary(total)
            : `${drawn.length} aufgefüllt · ${formatMoney(drawnSum)} · Summe ${formatMoney(total)}.`;
        } else {
          const distance =
            delta > 0
              ? `${formatMoney(delta)} über dem Ziel`
              : delta < 0
                ? // Free/custom: never claim success under target (underOk is 0 there).
                  underOk > 0 && total + underOk >= target
                  ? 'Ziel erreicht'
                  : `${formatMoney(-delta)} unter dem Ziel`
                : 'Ziel genau erreicht';
          const metTarget = total >= target;
          summaryEl.textContent = `${drawn.length} aufgefüllt · ${formatMoney(drawnSum)} · Summe ${formatMoney(total)} · ${distance}`;
          summaryTone = metTarget || (underOk > 0 && total + underOk >= target && total >= rules.minOrderCents)
            ? 'success'
            : '';
        }
      } else if (pool.length && reachable < target) {
        summaryEl.textContent = shortfallSummary(reachable);
      } else if (!drawn.length) {
        if (pool.length) {
          summaryEl.textContent =
            shortfallReason === 'holo'
              ? shortfallSummary(total)
              : `Noch ${formatMoney(target - startingCents())} bis ${formatMoney(target)}.`;
        } else {
          summaryEl.textContent = filtersActive()
            ? 'Mit diesen Filtern sind keine passenden Karten verfügbar.'
            : 'Aktuell sind keine passenden Karten verfügbar.';
        }
      }
      // Soft checkout note when cart-bound total is still under the store minimum.
      if (catalogReady && total < rules.minOrderCents) {
        const need = rules.minOrderCents - total;
        const minNote = `Hinweis: Mindestbestellwert ${formatMoney(rules.minOrderCents)} – noch ${formatMoney(need)} bis zur Bestellung.`;
        summaryEl.textContent = summaryEl.textContent ? `${summaryEl.textContent} ${minNote}` : minNote;
      }
      summaryEl.classList.toggle('is-error', summaryTone === 'error');
      summaryEl.classList.toggle('is-success', summaryTone === 'success');
    }
    if (addBtn) {
      const hasItems =
        drawn.length > 0 || wishCards.some((card) => !cartVariantIds.has(card.variantId));
      if (adding) {
        addBtn.disabled = true;
        addBtn.textContent = 'Wird in den Warenkorb gelegt …';
      } else if (scrubPending || drawing) {
        addBtn.disabled = true;
        addBtn.textContent = 'Verfügbarkeit wird geprüft …';
      } else if (awaitAddConfirm) {
        addBtn.disabled = !hasItems;
        addBtn.textContent = addBtnDefaultLabel;
      } else {
        addBtn.disabled = !hasItems;
        addBtn.textContent = addBtnDefaultLabel;
      }
    }
    if (!listEl) return;
    hideZoom();
    // Keep SSR skeletons until the catalog is ready so the footer stays below the fold.
    if (!catalogReady) return;
    listEl.replaceChildren();
    previewRows().forEach((card) => {
      const item = document.createElement('li');
      item.className = 'kl24-builder__card';
      item.dataset.variantId = card.variantId;
      item.dataset.role = card.role;
      const paint = energyPaint(card.energyType);
      item.style.setProperty('--energy', paint.color);
      const body = document.createElement('div');
      body.className = 'kl24-builder__copy';
      const badge = document.createElement('p');
      badge.className = `karten-auffueller__badge karten-auffueller__badge--${card.role}`;
      badge.textContent = card.role === 'cart' ? 'Im Warenkorb' : card.role === 'wish' ? 'Wunsch' : 'Aufgefüllt';
      const meta = document.createElement('p');
      meta.className = 'kl24-builder__set';
      meta.textContent = [card.setName || card.setCode, card.cardNumber].filter(Boolean).join(' · ');
      const price = document.createElement('p');
      price.className = 'kl24-builder__price';
      price.textContent = formatMoney(card.priceCents);
      body.append(badge, nameRow(card, 'kl24-builder__name'), meta);
      const photo = mountPhoto(card, card.role === 'cart' ? '' : card.cardName);
      photo.classList.add('kl24-builder__thumb');
      item.append(photo, body, price);
      if (card.role === 'fill') {
        const actions = document.createElement('div');
        actions.className = 'kl24-builder__actions';
        const swap = document.createElement('button');
        swap.type = 'button';
        swap.className = 'kl24-builder__swap';
        swap.textContent = 'Tauschen';
        swap.addEventListener('click', () => swapCard(card));
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'kl24-builder__icon-btn';
        remove.setAttribute('aria-label', 'Entfernen');
        remove.textContent = '×';
        remove.addEventListener('click', () => {
          drawn = drawn.filter((entry) => entry.variantId !== card.variantId);
          renderPreview();
        });
        actions.append(swap, remove);
        item.append(actions);
      }
      listEl.append(item);
    });
  };

  const catalogStatus = () =>
    catalog.length ? `${catalog.length} Einzelkarten geladen.` : 'Keine Einzelkarten gefunden.';

  const cancelScheduledDraw = () => {
    if (drawIdleHandle != null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(drawIdleHandle);
    }
    drawIdleHandle = null;
    if (drawTimeoutHandle != null) window.clearTimeout(drawTimeoutHandle);
    drawTimeoutHandle = null;
  };

  const finishDraw = (reroll, token) => {
    if (token !== drawToken || !catalogReady) {
      if (token === drawToken) drawing = false;
      return;
    }
    const fillStarted = performance.now();
    const target = targetCents();
    const gap = target == null ? 0 : target - startingCents();
    capHoloShare = !holoInput?.checked;
    const pool = filteredPool();
    const avoidKey = reroll ? selectionKey(drawn) : '';
    shortfallReason = '';
    const anchors = collectAnchors();
    let next = target == null ? [] : fillToTarget(pool, gap, anchors, { reroll, avoidKey });
    // Abort if a newer Würfeln started during the (possibly heavy) fill.
    if (token !== drawToken) return;
    // Cap retries: each fillToTarget on a large gap is costly; varyDrawByReplacement covers leftovers.
    if (reroll && avoidKey && gap > LARGE_GAP_CENTS) {
      for (let attempt = 0; attempt < 2 && selectionKey(next) === avoidKey; attempt += 1) {
        if (token !== drawToken) return;
        const retry = fillToTarget(pool, gap, anchors, { reroll: true, avoidKey });
        if (selectionKey(retry) !== avoidKey) {
          next = retry;
          break;
        }
      }
    }
    if (token !== drawToken) return;
    drawn = next;
    let stuck = false;
    if (reroll && avoidKey && selectionKey(drawn) === avoidKey) {
      const swapped = varyDrawByReplacement(drawn, pool, gap);
      if (swapped) drawn = swapped;
      else stuck = drawn.length > 0;
    }
    const fillMs = Math.round(performance.now() - fillStarted);
    const applyDrawn = (list, { ready = true } = {}) => {
      if (token !== drawToken) return;
      drawn = list;
      if (list.length > MAX_TOTAL) drawn = list.slice(0, MAX_TOTAL);
      const achieved = drawn.reduce((total, card) => total + card.priceCents, 0);
      const liveTarget = targetCents();
      const underOk = allowedUnderTarget(liveTarget == null ? gap : liveTarget);
      if (gap > 0 && achieved + underOk < gap) {
        markShortfall(pool, gap, achieved, drawn.length);
      } else {
        shortfallReason = '';
      }
      drawing = !ready;
      scrubPending = !ready;
      if (ready) {
        awaitAddConfirm = false;
        displayedSelectionKey = selectionKey(drawn);
        displayedSumCents = achieved;
      }
      if (statusEl) {
        statusEl.textContent = !ready
          ? 'Verfügbarkeit wird geprüft …'
          : stuck
            ? 'Keine andere Kombination mit diesen Filtern – Filter lockern?'
            : catalogStatus();
        statusEl.classList.toggle('kl24-builder__notice', stuck);
        statusEl.classList.toggle('is-error', stuck);
      }
      renderPreview();
    };
    // Hold Add until scrub+densify finish so cart matches the last shown pack.
    scrubPending = Boolean(window.Kl24InventoryReservation?.filterAvailableCards);
    drawing = true;
    if (statusEl) statusEl.textContent = 'Verfügbarkeit wird geprüft …';
    if (summaryEl && target != null) {
      summaryEl.textContent = 'Wird berechnet …';
      summaryEl.classList.remove('is-error', 'is-success');
    }
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = 'Verfügbarkeit wird geprüft …';
    }
    const gen = reserveGen;
    const finalize = (list) => {
      if (token !== drawToken || gen !== reserveGen) return;
      applyDrawn(list, { ready: true });
      try {
        window.__kl24AuffuellerPerf = {
          ...(window.__kl24AuffuellerPerf || {}),
          fillMs,
          finalMs: Math.round(performance.now() - fillStarted),
          cards: drawn.length,
          sumCents: packSum(drawn),
          token,
          at: Date.now(),
        };
      } catch {
        /* ignore */
      }
    };
    if (window.Kl24InventoryReservation?.filterAvailableCards) {
      const scrubPromise = ensureReservableCards(drawn, {
        refillGapCents: gap,
        force: false,
        gen,
      });
      const timeoutPromise = new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error('AVAIL_TIMEOUT')), AVAIL_TIMEOUT_MS);
      });
      void Promise.race([scrubPromise, timeoutPromise])
        .then((scrubbed) => {
          if (token !== drawToken || gen !== reserveGen) return;
          finalize(scrubbed.cards);
        })
        .catch((err) => {
          if (token !== drawToken || gen !== reserveGen) return;
          drawing = false;
          scrubPending = false;
          if (String(err?.message || err) === 'AVAIL_TIMEOUT') {
            drawn = [];
            displayedSumCents = 0;
            displayedSelectionKey = '';
            if (statusEl) {
              statusEl.textContent =
                'Verfügbarkeitsprüfung abgebrochen (Timeout 10 s). Bitte neue Auswahl laden.';
              statusEl.classList.add('is-error');
            }
            showFeedback(
              'Verfügbarkeitsprüfung dauerte zu lange. Bitte neue Auswahl laden.',
              'error'
            );
            renderPreview();
            return;
          }
          finalize(drawn);
        });
      return;
    }
    finalize(drawn);
  };

  const requestDraw = (reroll = false) => {
    if (!catalogReady) return;
    cancelScheduledDraw();
    const token = ++drawToken;
    const target = targetCents();
    const gap = target == null ? 0 : target - startingCents();
    drawing = target != null;
    if (drawing) {
      if (statusEl) statusEl.textContent = 'Wird berechnet …';
      if (summaryEl) {
        summaryEl.textContent = 'Wird berechnet …';
        summaryEl.classList.remove('is-error', 'is-success');
      }
    } else {
      drawing = false;
    }
    if (gap <= LARGE_GAP_CENTS) {
      finishDraw(reroll, token);
      return;
    }
    const run = () => {
      drawIdleHandle = null;
      drawTimeoutHandle = null;
      finishDraw(reroll, token);
    };
    if (typeof window.requestIdleCallback === 'function') {
      drawIdleHandle = window.requestIdleCallback(run, { timeout: 60 });
      return;
    }
    drawTimeoutHandle = window.setTimeout(run, 0);
  };

  const clearSwapMiss = () => {
    listEl?.querySelectorAll('[data-swap-miss]').forEach((node) => node.remove());
  };

  const showSwapMiss = (variantId) => {
    clearSwapMiss();
    if (!listEl || !variantId) return;
    const item = listEl.querySelector(`[data-variant-id="${CSS.escape(String(variantId))}"]`);
    if (!item) return;
    const note = document.createElement('p');
    note.className = 'kl24-builder__notice is-error';
    note.dataset.swapMiss = 'true';
    note.setAttribute('role', 'status');
    note.textContent = 'In dieser Preislage ist gerade keine andere Karte frei.';
    item.append(note);
  };

  const swapCard = (card) => {
    const blocked = new Set([
      ...drawn.map((entry) => entry.variantId),
      ...wishCards.map((entry) => entry.variantId),
    ]);
    const next = replacementFor(card, filteredPool(), blocked);
    if (!next) {
      showSwapMiss(card.variantId);
      return;
    }
    clearSwapMiss();
    drawn = drawn.map((entry) => (entry.variantId === card.variantId ? next : entry));
    renderPreview();
  };

  const renderSetFilters = (preselected) => {
    if (!setsEl) return;
    const byCode = new Map();
    catalog.forEach((card) => {
      if (!card.setCode || byCode.has(card.setCode)) return;
      byCode.set(card.setCode, card.setName || card.setCode);
    });
    preselected.forEach((code) => {
      if (!byCode.has(code)) byCode.set(code, code);
    });
    const rows = [...byCode.entries()].sort((a, b) => a[1].localeCompare(b[1], 'de'));
    setsEl.replaceChildren();
    rows.forEach(([code, label]) => {
      const row = document.createElement('label');
      row.className = 'kl24-builder__check';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = code;
      input.checked = preselected.has(code);
      const text = document.createElement('span');
      text.textContent = label;
      row.append(input, text);
      setsEl.append(row);
    });
  };

  const renderEnergyChips = (values) => {
    const host = root.querySelector('[data-fill-energies]');
    if (!host || !energySelect) return;
    const available = new Set((values || []).map((value) => String(value || '')));
    const current = energySelect.value || '';
    // Chips are SSR'd in Liquid; bind once and only sync pressed/disabled state (CLS).
    if (!host.dataset.fillEnergyBound) {
      host.dataset.fillEnergyBound = '1';
      host.querySelectorAll('[data-fill-energy]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          const value = button.getAttribute('data-fill-energy') || '';
          if (value && ![...energySelect.options].some((opt) => opt.value === value)) return;
          energySelect.value = value;
          host.querySelectorAll('[data-fill-energy]').forEach((chip) => {
            const on = chip === button;
            chip.classList.toggle('is-active', on);
            chip.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          syncLotEnergy(value);
          energySelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
    host.hidden = false;
    host.querySelectorAll('[data-fill-energy]').forEach((button) => {
      const value = button.getAttribute('data-fill-energy') || '';
      const inStock = !value || available.has(value);
      button.disabled = Boolean(value) && !inStock;
      button.classList.toggle('is-low-stock', Boolean(value) && !inStock);
      const active = value === current;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    syncLotEnergy(current);
  };

  const renderSelectOptions = (select, wrap, values) => {
    if (!select || !wrap) return;
    const current = select.value;
    select.replaceChildren();
    const any = document.createElement('option');
    any.value = '';
    any.textContent = 'Egal';
    select.append(any);
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.append(option);
    });
    if (values.includes(current)) select.value = current;
    wrap.hidden = values.length < 1;
  };

  const renderDynamicFilters = () => {
    const MIN_ENERGY_STOCK = 10;
    const counts = new Map();
    catalog.forEach((card) => {
      if (!card.energyType) return;
      counts.set(card.energyType, (counts.get(card.energyType) || 0) + 1);
    });
    const energies = [...counts.entries()]
      .filter(([, count]) => count >= MIN_ENERGY_STOCK)
      .map(([value]) => value)
      .sort((a, b) => a.localeCompare(b, 'de'));
    renderSelectOptions(energySelect, energyWrap, energies);
    renderEnergyChips(energies);
    const types = [...new Set(catalog.map((card) => card.productType).filter(Boolean))].filter(
      (value) => value.toLowerCase() !== 'einzelkarte'
    );
    renderSelectOptions(typeSelect, typeWrap, types.sort((a, b) => a.localeCompare(b, 'de')));
    if (typeWrap && types.length < 2) typeWrap.hidden = true;
  };

  const searchWishes = () => {
    if (!wishResults || !wishSearch) return;
    const query = wishSearch.value.trim().toLowerCase();
    wishResults.replaceChildren();
    if (query.length < 2) {
      wishResults.hidden = true;
      return;
    }
    const taken = new Set([...wishCards, ...drawn].map((card) => card.variantId));
    const hits = catalog
      .filter((card) => card.available && card.priceCents > 0 && !taken.has(card.variantId))
      .filter((card) => {
        const hay = [card.cardName, card.setName, card.setCode, card.cardNumber, card.title, card.sku]
          .join(' ')
          .toLowerCase();
        return hay.includes(query);
      })
      .slice(0, 8);
    wishResults.hidden = false;
    if (!hits.length) {
      const empty = document.createElement('li');
      empty.className = 'karten-auffueller__result-meta';
      empty.textContent = 'Keine Karte gefunden.';
      wishResults.append(empty);
      return;
    }
    hits.forEach((card) => {
      const item = document.createElement('li');
      item.className = 'karten-auffueller__result';
      const body = document.createElement('div');
      body.className = 'karten-auffueller__result-body';
      const meta = document.createElement('p');
      meta.className = 'karten-auffueller__result-meta';
      meta.textContent = `${metaLine(card)} · ${formatMoney(card.priceCents)}`;
      body.append(nameRow(card, 'karten-auffueller__result-name'), meta);
      const actions = document.createElement('div');
      actions.className = 'karten-auffueller__result-actions';
      const pick = document.createElement('button');
      pick.type = 'button';
      pick.className = 'karten-auffueller__btn karten-auffueller__btn--small';
      pick.textContent = 'Dazu';
      pick.addEventListener('click', () => {
        if (!wishCards.some((entry) => entry.variantId === card.variantId)) {
          wishCards = [...wishCards, card];
        }
        wishSearch.value = '';
        wishResults.hidden = true;
        wishResults.replaceChildren();
        renderWishes();
        requestDraw();
      });
      actions.append(pick);
      item.append(mountPhoto(card, card.cardName), body, actions);
      wishResults.append(item);
    });
  };

  const applyZielFromUrl = () => {
    if (zielTouched) return;
    const raw = new URLSearchParams(window.location.search).get('ziel');
    const radios = root.querySelectorAll('[data-fill-mode]');
    const select = (value) => {
      radios.forEach((radio) => {
        radio.checked = radio.value === value;
      });
    };
    if (raw == null || raw === '') {
      select(cartSubtotal < rules.minOrderCents ? 'minimum' : 'free');
      return;
    }
    const key = raw.trim().toLowerCase();
    if (key === 'minimum' || key === 'mindest' || key === 'min') {
      select('minimum');
      return;
    }
    if (key === 'gratis' || key === 'free' || key === 'versand') {
      select('free');
      return;
    }
    const amount = Number(key.replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) {
      select('free');
      return;
    }
    const asCents = Math.round(amount * 100);
    if (asCents === rules.minOrderCents || amount === rules.minOrderCents) {
      select('minimum');
      return;
    }
    if (asCents === rules.freeShippingCents || amount === rules.freeShippingCents) {
      select('free');
      return;
    }
    select('custom');
    if (amountInput) amountInput.value = String(amount).replace('.', ',');
  };

  const applyLanguageFromUrl = () => {
    const raw = (
      new URLSearchParams(window.location.search).get('sprache') ||
      new URLSearchParams(window.location.search).get('lang') ||
      ''
    ).toLowerCase();
    if (raw === 'en' || raw === 'englisch' || raw === 'english') language = 'en';
    else if (raw === 'de' || raw === 'deutsch' || raw === 'german') language = 'de';
    root.querySelectorAll('[data-fill-language]').forEach((button) => {
      const active = button.dataset.fillLanguage === language;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const isStockError = (error) => {
    const text = `${error?.message || ''} ${error?.description || ''}`.toLowerCase();
    return (
      error?.status === 422 ||
      error?.stock === true ||
      text.includes('sold out') ||
      text.includes('ausverkauft') ||
      text.includes('nicht verfügbar') ||
      text.includes('not available') ||
      text.includes('nicht genug') ||
      text.includes('inventory') ||
      text.includes('reserv')
    );
  };

  const humanCartError = (error) => {
    const raw = String(error?.description || error?.message || '').trim();
    const lower = raw.toLowerCase();
    if (!raw) return 'Der Warenkorb konnte nicht aktualisiert werden.';
    if (
      lower.includes('sold out') ||
      lower.includes('ausverkauft') ||
      lower.includes('not enough') ||
      lower.includes('nicht genug') ||
      lower.includes('unavailable') ||
      lower.includes('nicht verfügbar')
    ) {
      return 'Mindestens eine Karte ist nicht mehr verfügbar.';
    }
    if (lower.includes('reserv')) return 'Eine Karte ist gerade reserviert. Wir haben die Auswahl angepasst.';
    if (/[äöüß]|nicht|bitte|warenkorb/i.test(raw)) return raw;
    return 'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuche es noch einmal.';
  };

  const cardMentioned = (card, message) => {
    const hay = String(message || '').toLowerCase();
    const name = String(card.cardName || '').toLowerCase();
    if (name.length > 2 && hay.includes(name)) return true;
    const sku = String(card.sku || '').toLowerCase();
    return Boolean(sku && hay.includes(sku));
  };

  const cardStillAvailable = async (card) => {
    try {
      const res = await fetch(`/products/${encodeURIComponent(card.handle)}.js`, {
        credentials: 'same-origin',
      });
      if (!res.ok) return false;
      const product = await res.json();
      const variant = (product.variants || []).find((entry) => String(entry.id) === String(card.variantId));
      return Boolean(variant && variant.available !== false);
    } catch {
      return true;
    }
  };

  const unavailableCards = async (cards) => {
    const missing = [];
    const queue = [...cards];
    const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (queue.length) {
        const card = queue.shift();
        if (!card) return;
        const ok = await cardStillAvailable(card);
        if (!ok) missing.push(card);
      }
    });
    await Promise.all(workers);
    return missing;
  };

  /**
   * Warm availability cache for a candidate slice (not full catalog) so the first
   * scrub of the chosen pack hits cache / fewer parallel waves. Does not block UI long:
   * capped sample + force:false.
   */
  const warmAvailabilityPrefetch = async (pool) => {
    const api = window.Kl24InventoryReservation;
    if (!api?.filterAvailableCards || !pool?.length) return null;
    const t0 = performance.now();
    const asc = pool
      .slice()
      .filter((c) => c?.variantId && (c.priceCents || 0) > 0)
      .sort(
        (a, b) =>
          (a.priceCents || 0) - (b.priceCents || 0) ||
          String(a.variantId).localeCompare(String(b.variantId))
      );
    if (!asc.length) return null;
    const mid = Math.floor(asc.length * 0.35);
    const sample = [];
    const seen = new Set();
    const push = (card) => {
      if (!card || seen.has(card.variantId)) return;
      seen.add(card.variantId);
      sample.push(card);
    };
    asc.slice(0, 180).forEach(push);
    asc.slice(mid, mid + 120).forEach(push);
    asc.slice(Math.max(0, asc.length - 120)).forEach(push);
    try {
      await api.filterAvailableCards(sample, { force: false });
      const ms = Math.round(performance.now() - t0);
      try {
        window.__kl24AuffuellerPerf = {
          ...(window.__kl24AuffuellerPerf || {}),
          availWarm: { ms, n: sample.length },
        };
      } catch {
        /* ignore */
      }
      return { ms, n: sample.length };
    } catch (error) {
      console.warn('[Auffüller] availability warm:', error?.message || error);
      return null;
    }
  };

  /**
   * Drop cards held by other sessions; replace from pool so €-target / count stay filled.
   * heldIds persists across rounds so replacements never re-pick known-held variants.
   */
  const ensureReservableCards = async (
    cards,
    { refillGapCents = 0, force = false, gen = reserveGen } = {}
  ) => {
    const api = window.Kl24InventoryReservation;
    if (!api?.filterAvailableCards || !cards?.length) {
      return { cards: cards || [], notes: [] };
    }
    const deadline = performance.now() + AVAIL_TIMEOUT_MS;
    const scrubT0 = performance.now();
    let availCalls = 0;
    const timedOut = () => performance.now() >= deadline;
    const alive = () => gen === reserveGen && !timedOut();
    const filterOnce = async (list, forceFlag) => {
      availCalls += 1;
      return api.filterAvailableCards(list, { force: Boolean(forceFlag) });
    };
    const notes = [];
    let working = cards.slice();
    const wishIds = new Set(wishCards.map((card) => card.variantId));
    const heldIds = new Set();
    let hadBlocked = false;
    const underOk =
      refillGapCents > 0
        ? allowedUnderTarget(startingCents() + refillGapCents)
        : fillToleranceCents();
    // Warm-cache fast path: if every card is already known free, skip availability round-trips.
    if (
      !force &&
      refillGapCents > 0 &&
      withinOvershoot(packSum(working), refillGapCents) &&
      typeof api.getCachedAvailability === 'function'
    ) {
      const ttlMs = 45_000;
      const now = Date.now();
      const allFreshFree = working.every((card) => {
        const hit = api.getCachedAvailability({
          variantId: card.variantId,
          sku: card.sku,
        });
        if (!hit || now - (hit.fetchedAt || 0) > ttlMs) return false;
        return !api.isHeldByOthers?.(hit);
      });
      if (allFreshFree) {
        working = trimExcessCards(working, refillGapCents);
        try {
          window.__kl24AuffuellerPerf = {
            ...(window.__kl24AuffuellerPerf || {}),
            availScrub: {
              ms: Math.round(performance.now() - scrubT0),
              calls: 0,
              cards: working.length,
              earlyExit: true,
              cacheHit: true,
            },
          };
        } catch {
          /* ignore */
        }
        return { cards: working.slice(0, MAX_TOTAL), notes, heldIds, aborted: false };
      }
    }
    // Preview: 1 round; add-time: at most 2. Prefer returning early when €-window already met.
    const maxRounds = force ? 2 : 1;

    const poolForReplace = (used) =>
      filteredPool().filter(
        (card) => !heldIds.has(card.variantId) && !used.has(card.variantId) && !wishIds.has(card.variantId)
      );

    const idsOf = (list) => new Set((list || []).map((card) => String(card.variantId)));

    for (let round = 0; round < maxRounds; round += 1) {
      if (!alive()) return { cards: working.slice(0, MAX_TOTAL), notes, heldIds, aborted: true };
      const { keep, blocked } = await filterOnce(working, force && round === 0);
      if (!alive()) return { cards: keep.slice(0, MAX_TOTAL), notes, heldIds, aborted: true };
      blocked.forEach((card) => heldIds.add(card.variantId));
      if (!blocked.length) {
        working = keep;
        break;
      }
      hadBlocked = true;
      let next = keep.slice();
      const used = new Set([...next.map((card) => card.variantId), ...heldIds]);
      for (const card of blocked) {
        if (wishIds.has(card.variantId)) {
          wishCards = wishCards.filter((entry) => entry.variantId !== card.variantId);
          notes.push(
            `„${card.cardName}“ ist gerade bei einem anderen Kunden reserviert und wurde aus den Wunschkarten genommen.`
          );
          continue;
        }
        if (next.length >= MAX_TOTAL) {
          notes.push(`„${card.cardName}“ ist gerade reserviert und wurde entfernt.`);
          continue;
        }
        let placed = false;
        for (let tryN = 0; tryN < 10 && !placed; tryN += 1) {
          const replacement = replacementFor(card, poolForReplace(used), used);
          if (!replacement) break;
          if (heldIds.has(replacement.variantId)) {
            used.add(replacement.variantId);
            continue;
          }
          used.add(replacement.variantId);
          next.push(replacement);
          notes.push(
            `„${card.cardName}“ ist gerade reserviert. Stattdessen liegt „${replacement.cardName}“ in der Auswahl.`
          );
          placed = true;
        }
        if (!placed) {
          notes.push(`„${card.cardName}“ ist gerade reserviert und wurde entfernt.`);
        }
      }
      working = next.slice(0, MAX_TOTAL);
    }

    // Re-check replacements only; then densify from free pool (sync) and check only deltas.
    if (hadBlocked && alive()) {
      const { keep, blocked } = await filterOnce(working, Boolean(force));
      blocked.forEach((card) => heldIds.add(card.variantId));
      working = keep.slice(0, MAX_TOTAL);
    }

    if (refillGapCents > 0 && alive()) {
      // Already in window after scrub → skip densify / extra availability (main latency win).
      // Strict free/custom: underOk=0 → must be ≥ Ziel (or within overshoot).
      if (
        withinOvershoot(packSum(working), refillGapCents) ||
        packSum(working) + underOk >= refillGapCents
      ) {
        try {
          window.__kl24AuffuellerPerf = {
            ...(window.__kl24AuffuellerPerf || {}),
            availScrub: {
              ms: Math.round(performance.now() - scrubT0),
              calls: availCalls,
              cards: working.length,
              earlyExit: true,
            },
          };
        } catch {
          /* ignore */
        }
        return { cards: working.slice(0, MAX_TOTAL), notes, heldIds, aborted: !alive() };
      }
      // Best pack across passes — never finish worse than the post-scrub remnant or a rebuild.
      // Prefer one selectPack rebuild when far under (faster than densify on weak mobile CPUs).
      let best = working.slice();
      for (let pass = 0; pass < 3 && alive(); pass += 1) {
        if (
          withinOvershoot(packSum(working), refillGapCents) ||
          packSum(working) + underOk >= refillGapCents
        ) {
          break;
        }
        const beforeIds = idsOf(working);
        const used = new Set([
          ...working.map((card) => card.variantId),
          ...wishCards.map((card) => card.variantId),
          ...heldIds,
        ]);
        const pool = filteredPool().filter(
          (card) => !used.has(card.variantId) && !heldIds.has(card.variantId)
        );
        if (!pool.length) break;

        const remnantSum = packSum(working);
        const remnantWeak = remnantSum < refillGapCents * 0.55 || working.length < 40;
        if (remnantWeak) {
          // Rebuild via harness selectPack (not densify of a scrubbed mini-pack).
          const packed = selectPackCore(pool, refillGapCents, {
            heldIds: [...heldIds],
            random: Math.random,
          });
          working = betterPack(
            packed.ok ? packed.cards : packed.cards?.length ? packed.cards : working,
            working,
            refillGapCents
          ).slice(0, MAX_TOTAL);
          if (!packed.ok && packSum(working) + underOk < refillGapCents) {
            working = topUpToMeetTarget(working, pool, refillGapCents, { budgetMs: 60 }).slice(
              0,
              MAX_TOTAL
            );
          }
        } else {
          // Close to Ziel: cheap top-up only (skip densify — main mobile wall-time saver).
          working = topUpToMeetTarget(working, pool, refillGapCents, { budgetMs: 60 }).slice(
            0,
            MAX_TOTAL
          );
          if (packSum(working) + underOk < refillGapCents) {
            const packed = selectPackCore(pool, refillGapCents, {
              heldIds: [...heldIds],
              random: Math.random,
            });
            if (packed.ok || packed.cards?.length) {
              working = betterPack(
                packed.ok ? packed.cards : packed.cards,
                working,
                refillGapCents
              ).slice(0, MAX_TOTAL);
            }
          }
        }
        working = trimExcessCards(working, refillGapCents);

        const delta = working.filter((card) => !beforeIds.has(String(card.variantId)));
        if (delta.length) {
          const checked = await filterOnce(delta, Boolean(force) && pass === 0);
          if (!alive()) {
            return {
              cards: betterPack(working, best, refillGapCents).slice(0, MAX_TOTAL),
              notes,
              heldIds,
              aborted: true,
            };
          }
          checked.blocked.forEach((card) => heldIds.add(card.variantId));
          const keptDelta = new Set(checked.keep.map((card) => String(card.variantId)));
          working = working.filter(
            (card) => beforeIds.has(String(card.variantId)) || keptDelta.has(String(card.variantId))
          );
          // After delta scrub removed cards: immediately top up again toward Ziel.
          if (packSum(working) + underOk < refillGapCents && alive()) {
            const used2 = new Set([
              ...working.map((card) => card.variantId),
              ...wishCards.map((card) => card.variantId),
              ...heldIds,
            ]);
            const pool2 = filteredPool().filter(
              (card) => !used2.has(card.variantId) && !heldIds.has(card.variantId)
            );
            if (pool2.length) {
              const beforeTop = idsOf(working);
              working = topUpToMeetTarget(working, pool2, refillGapCents, { budgetMs: 50 }).slice(
                0,
                MAX_TOTAL
              );
              const topDelta = working.filter((card) => !beforeTop.has(String(card.variantId)));
              if (topDelta.length && alive()) {
                const topChecked = await filterOnce(topDelta, false);
                topChecked.blocked.forEach((card) => heldIds.add(card.variantId));
                const keptTop = new Set(topChecked.keep.map((card) => String(card.variantId)));
                working = working.filter(
                  (card) =>
                    beforeTop.has(String(card.variantId)) || keptTop.has(String(card.variantId))
                );
              }
            }
          }
        }
        best = betterPack(working, best, refillGapCents);
        working = best.slice();
      }
      working = trimExcessCards(betterPack(working, best, refillGapCents), refillGapCents);
    }

    working = trimExcessCards(working, refillGapCents);
    try {
      window.__kl24AuffuellerPerf = {
        ...(window.__kl24AuffuellerPerf || {}),
        availScrub: {
          ms: Math.round(performance.now() - scrubT0),
          calls: availCalls,
          cards: working.length,
          earlyExit: false,
        },
      };
    } catch {
      /* ignore */
    }
    return { cards: working.slice(0, MAX_TOTAL), notes, heldIds, aborted: !alive() };
  };

  const postAdd = async (cards, { withSections = false } = {}) => {
    const items = cards.map((card) => ({ id: Number(card.variantId), quantity: 1 }));
    // Bulk add; sections omitted on the happy path (we navigate to /cart). sync-cart reconciles holds.
    const payload = { items };
    if (withSections) {
      payload.sections = CART_SECTIONS;
      payload.sections_url = window.location.pathname;
    }
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(body.description || body.message || 'Warenkorb konnte nicht aktualisiert werden.');
      err.status = response.status;
      err.description = body.description || '';
      err.stock = response.status === 422 || isStockError(err);
      throw err;
    }
    return body;
  };

  const applyDrawerSection = (parsed) => {
    try {
      const html = parsed?.sections?.['cart-drawer'];
      const drawer = document.querySelector('cart-drawer');
      if (!html || !drawer || typeof drawer.getSectionInnerHTML !== 'function') return;
      const target = document.querySelector('#CartDrawer');
      if (!target) return;
      const inner = drawer.getSectionInnerHTML(html, '#CartDrawer');
      if (!inner) return;
      target.innerHTML = inner;
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') {
        overlay.addEventListener('click', drawer.close.bind(drawer));
      }
    } catch (error) {
      console.warn('[Auffüller] Warenkorb-Drawer:', error);
    }
  };

  const applyChrome = async (parsed) => {
    applyDrawerSection(parsed);
    const chrome = window.Kl24InventoryReservation;
    if (chrome?.refreshCartChrome) {
      await chrome.refreshCartChrome(parsed, { openDrawer: false, source: 'karten-auffueller' });
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  };

  const refreshCart = async () => {
    const res = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return;
    const cart = await res.json();
    applyCart(cart);
    window.Kl24CartEnhancements?.syncCartState?.(cart);
  };

  const replaceFailedCards = async (batch, error) => {
    const message = `${error?.description || ''} ${error?.message || ''}`;
    const mentioned = batch.filter((card) => cardMentioned(card, message));
    const targets = mentioned.length ? mentioned : await unavailableCards(batch);
    if (!targets.length) return { batch, notes: [], changed: false };
    const notes = [];
    let next = batch.slice();
    const wishIds = new Set(wishCards.map((card) => card.variantId));
    for (const card of targets) {
      const blocked = new Set(next.map((entry) => entry.variantId));
      if (wishIds.has(card.variantId)) {
        next = next.filter((entry) => entry.variantId !== card.variantId);
        wishCards = wishCards.filter((entry) => entry.variantId !== card.variantId);
        notes.push(`„${card.cardName}“ ist nicht mehr verfügbar und wurde aus den Wunschkarten genommen.`);
        continue;
      }
      const replacement = replacementFor(card, filteredPool(), blocked);
      if (replacement) {
        next = next.map((entry) => (entry.variantId === card.variantId ? replacement : entry));
        drawn = drawn.map((entry) => (entry.variantId === card.variantId ? replacement : entry));
        notes.push(`„${card.cardName}“ war weg. Stattdessen liegt „${replacement.cardName}“ in der Auswahl.`);
      } else {
        next = next.filter((entry) => entry.variantId !== card.variantId);
        drawn = drawn.filter((entry) => entry.variantId !== card.variantId);
        notes.push(`„${card.cardName}“ ist nicht mehr verfügbar und wurde entfernt.`);
      }
    }
    renderWishes();
    renderPreview();
    return { batch: next, notes, changed: true };
  };

  const addAll = async () => {
    const debug = (phase, extra = {}) => {
      try {
        window.__kl24AddDebug = {
          phase,
          drawn: drawn.length,
          wish: wishCards.length,
          adding,
          at: Date.now(),
          ...extra,
        };
      } catch {
        /* ignore */
      }
    };
    debug('click', { adding, scrubPending, awaitAddConfirm });
    if (adding) return;
    if (scrubPending || drawing) {
      showFeedback('Verfügbarkeit wird noch geprüft. Bitte einen Moment warten.', 'error');
      return;
    }
    let batch = [];
    const seen = new Set();
    [...wishCards, ...drawn].forEach((card) => {
      if (!card.variantId || seen.has(card.variantId) || cartVariantIds.has(card.variantId)) return;
      seen.add(card.variantId);
      batch.push(card);
    });
    if (!batch.length) {
      debug('empty-batch');
      showFeedback('Nichts zum Hinzufügen. Die Karten liegen schon im Warenkorb oder die Auswahl ist leer.', 'error');
      return;
    }
    const beforeKey = selectionKey(batch);
    const beforeSum = packSum(batch);
    const beforeCount = batch.length;
    adding = true;
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = 'Wird in den Warenkorb gelegt …';
    }
    const notes = [];
    const added = [];
    let lastParsed = null;
    debug('start', { initial: batch.length, batch: batch.length, added: 0 });
    try {
      const target = targetCents();
      const gap = target == null ? 0 : Math.max(0, target - startingCents());
      // Cancel preview scrub so addAll does not race the same availability API.
      cancelScheduledDraw();
      drawToken += 1;
      reserveGen += 1;
      drawing = false;
      scrubPending = false;
      const addGen = reserveGen;
      // Confirmed pack: warm-cache check only. First click: force refresh.
      const scrubbed = await ensureReservableCards(batch, {
        refillGapCents: gap,
        force: !awaitAddConfirm,
        gen: addGen,
      });
      debug('scrubbed', {
        aborted: Boolean(scrubbed.aborted),
        kept: scrubbed.cards?.length || 0,
        notes: scrubbed.notes?.length || 0,
      });
      if (scrubbed.aborted) {
        showFeedback('Auswahl wurde abgebrochen. Bitte erneut in den Warenkorb legen.', 'error');
        return;
      }
      notes.push(...scrubbed.notes);
      batch = scrubbed.cards;
      drawn = batch.filter((card) => !wishCards.some((w) => w.variantId === card.variantId));
      const afterKey = selectionKey(batch);
      const afterSum = packSum(batch);
      const afterCount = batch.length;
      // Ignore tiny € jitter (e.g. 5,25 → 5,26) — only reconfirm on real pack changes.
      const RECONFIRM_SUM_CENTS = 5;
      const changed =
        afterCount !== beforeCount || Math.abs(afterSum - beforeSum) >= RECONFIRM_SUM_CENTS;
      renderWishes();
      renderPreview();
      if (!batch.length) {
        showFeedback(compactReserveNotes(notes) || 'Keine freien Karten für den Auffüller.', 'error');
        debug('empty-after-scrub');
        return;
      }
      // If add-time check changed the pack, show the new totals and require a second click.
      if (changed) {
        displayedSelectionKey = afterKey;
        displayedSumCents = afterSum;
        awaitAddConfirm = true;
        const msg = `Auswahl aktualisiert: ${afterCount} Karten / ${formatMoney(afterSum)}. Bitte erneut „${addBtnDefaultLabel}“ tippen.`;
        showFeedback(msg, 'success');
        if (summaryEl) {
          summaryEl.textContent = msg;
          summaryEl.classList.remove('is-error');
          summaryEl.classList.add('is-success');
        }
        debug('reconfirm', { beforeCount, afterCount, beforeSum, afterSum });
        return;
      }
      awaitAddConfirm = false;
      displayedSelectionKey = afterKey;
      displayedSumCents = afterSum;
      const pending = batch.slice();
      debug('posting', { pending: pending.length, sum: afterSum });
      while (pending.length) {
        let current = pending.splice(0, ADD_BATCH);
        let placed = false;
        for (let attempt = 0; attempt < 4 && !placed; attempt += 1) {
          try {
            lastParsed = await postAdd(current, { withSections: false });
            added.push(...current);
            placed = true;
            debug('posted', { chunk: current.length, added: added.length });
          } catch (error) {
            debug('post-error', {
              attempt,
              status: error?.status,
              stock: Boolean(error?.stock),
              message: String(error?.message || '').slice(0, 160),
            });
            if (!isStockError(error) || attempt === 3) {
              if (lastParsed) await applyChrome(lastParsed);
              await refreshCart();
              const addedIds = new Set(added.map((card) => card.variantId));
              wishCards = wishCards.filter((card) => !addedIds.has(card.variantId));
              drawn = drawn.filter((card) => !addedIds.has(card.variantId));
              renderWishes();
              renderPreview();
              const prefix = added.length
                ? `${added.length} ${added.length === 1 ? 'Karte liegt' : 'Karten liegen'} im Warenkorb. `
                : '';
              showFeedback(`${prefix}${humanCartError(error)}`, added.length ? 'success' : 'error');
              return;
            }
            const replaced = await replaceFailedCards(current, error);
            notes.push(...replaced.notes);
            if (!replaced.changed) {
              showFeedback(humanCartError(error), 'error');
              return;
            }
            current = replaced.batch;
            if (!current.length) placed = true;
          }
        }
      }
      if (!added.length) {
        showFeedback(compactReserveNotes(notes) || 'Die Karten sind nicht mehr verfügbar.', 'error');
        debug('nothing-added');
        return;
      }
      void window.Kl24InventoryReservation?.reconcileCartReservations?.();
      debug('navigate', { added: added.length });
      window.location.assign('/cart');
      return;
    } catch (error) {
      debug('throw', { message: String(error?.message || error).slice(0, 200) });
      showFeedback(humanCartError(error), 'error');
    } finally {
      adding = false;
      if (addBtn) addBtn.textContent = addBtnDefaultLabel;
      renderPreview();
    }
  };

  const boot = async () => {
    if (minLabel) minLabel.innerHTML = `Mindest&shy;bestellwert (${formatMoney(rules.minOrderCents)})`;
    if (freeLabel) freeLabel.textContent = `Gratis-Versand (${formatMoney(rules.freeShippingCents)})`;
    applyLanguageFromUrl();

    const cartPromise = fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);

    try {
      if (window.Kl24SinglesPool?.load) {
        const [cart, loaded] = await Promise.all([
          cartPromise,
          window.Kl24SinglesPool.load({
            handle: collectionHandle,
            setsIndexUrl: root.dataset.setsIndex,
          }),
        ]);
        latestCart = cart;
        setIndex = loaded.sets || [];
        catalog = loaded.cards || [];
        applySetNames();
        writeCache(catalog);
      } else {
        const cached = readCache();
        const [cart, index, rawProducts] = await Promise.all([
          cartPromise,
          loadSetIndex(),
          cached ? Promise.resolve(null) : loadRawProducts(),
        ]);
        latestCart = cart;
        setIndex = index;
        if (cached) {
          catalog = cached;
        } else {
          const seen = new Set();
          catalog = (rawProducts || []).map(normalizeProduct).filter((card) => {
            if (!card.variantId || seen.has(card.variantId)) return false;
            seen.add(card.variantId);
            return true;
          });
          applySetNames();
          writeCache(catalog);
        }
        if (cached) applySetNames();
      }
      applyCart(latestCart);
      catalogReady = true;
      renderSetFilters(resolveUrlSetCodes());
      renderDynamicFilters();
      applyZielFromUrl();
      if (statusEl) {
        const failed = !catalog.length;
        statusEl.textContent = failed ? 'Keine Einzelkarten gefunden.' : `${catalog.length} Einzelkarten geladen.`;
        statusEl.classList.toggle('kl24-builder__notice', failed);
        statusEl.classList.toggle('is-error', failed);
      }
      // Warm candidate slice in background; scrub only checks the chosen pack (delta).
      void warmAvailabilityPrefetch(catalog);
      requestDraw();
    } catch (error) {
      console.warn('[Auffüller]', error);
      if (statusEl) {
        statusEl.textContent = 'Die Karten konnten nicht geladen werden. Bitte lade die Seite neu.';
        statusEl.classList.add('kl24-builder__notice', 'is-error');
      }
    }
  };

  root.querySelector('[data-fill-max-group]')?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-fill-max]');
    if (!button) return;
    root.querySelectorAll('[data-fill-max]').forEach((chip) => {
      const active = chip === button;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    requestDraw();
  });

  root.querySelector('[data-fill-language-group]')?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-fill-language]');
    if (!button) return;
    language = button.dataset.fillLanguage || '';
    root.querySelectorAll('[data-fill-language]').forEach((chip) => {
      const active = chip === button;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    requestDraw();
  });

  root.querySelectorAll('[data-fill-mode]').forEach((radio) => {
    radio.addEventListener('change', () => {
      zielTouched = true;
      requestDraw();
    });
  });

  amountInput?.addEventListener('input', () => {
    const custom = root.querySelector('[data-fill-mode][value="custom"]');
    if (custom) custom.checked = true;
    zielTouched = true;
    window.clearTimeout(amountTimer);
    amountTimer = window.setTimeout(requestDraw, 350);
  });

  setsEl?.addEventListener('change', () => requestDraw());
  energySelect?.addEventListener('change', () => requestDraw());
  typeSelect?.addEventListener('change', () => requestDraw());
  holoInput?.addEventListener('change', () => requestDraw());
  commonsInput?.addEventListener('change', () => requestDraw());
  cartSetInput?.addEventListener('change', () => requestDraw());
  excludeCartInput?.addEventListener('change', () => requestDraw());
  rollBtn?.addEventListener('click', () => {
    showFeedback('', '');
    requestDraw(true);
  });
  addBtn?.addEventListener('click', () => {
    void addAll();
  });
  wishSearch?.addEventListener('input', () => searchWishes());

  void boot();
})();
